import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, Optional, inject, ViewChild, ChangeDetectorRef, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { AgentOrchestratorService, AgentChatMessage } from '../../services/agent-orchestrator/agent-orchestrator.service';
import { ChatbotComponent } from '../../../components/chatbot/chatbot.component';
import { ChatbotService } from '../../../../services/chatbot-services/chatbot.service';
import { AgentChatInterfaceComponent } from '../agent-chat-interface/agent-chat-interface.component';
import { AgentConfigService } from '../../services/agent-config/agent-config.service';
import { LoggerService } from '../../../../services/logger-services/logger.service';
import { GetAgentResponseHistoryRequest } from '../../models';
import { SourceContext } from '../../../models/source-panel.models';
import { environment } from 'src/environments/environment';
import { GetAgentResponseHistoryService } from '../../services/agent-response-history';
import config from '../../../../config/config.json';
import moduleInfo from '../../../../config/module_info.json';
import { StreamingApiServicesService } from '../../services/agent-api-services/agent-streaming-api-services/agent-streaming-api.service';
import { StreamingStateService } from '../../../../shared/services/streaming-state/streaming-state.service';
import { EngagingLoaderStep, THINKING_STEP_ACTION } from '../../../../shared/components/engaging-loader/engaging-loader.component';
import { PromptSectionComponent } from '../../../../shared/components/prompt-section/prompt-section.component';
import { AgentPromptPrefixService } from '../../services/agent-prompt-prefix/agent-prompt-prefix.service';
import { SingleShareConversationService, SharePopupData } from '../../services/agent-threads/single-share-conversation/single-share-conversation.service';
import { ConfigurationService } from '../../../../services/configuration/configuration.service';
import { AgentApiService } from '../../../../shared/services/agent-api/agent-api.service';
import { ApiService } from '../../../../services/api/api.service';
import { TokenService } from '../../../../shared/services/token/token.service';
import { ChatAutoScrollService } from '../../../../shared/services/chat-auto-scroll/chat-auto-scroll.service';
import { TranslateService } from '@ngx-translate/core';
import type { AgentFilePreviewRequest } from '../../../../shared/components/agent-file-attachment-card/agent-file-attachment-card.component';
import { AutoHideToast } from '../../../../shared/helpers/auto-hide-toast';
import {
  SaveAgentResponseService,
  buildSaveAgentResponsePayload,
  extractStreamCompletionMeta
} from '../../services/save-agent-response';
import {
  mapHistoryResponseToAgentMessage,
} from '../../services/ria-history-file-url';
import { RetrievalModePreferenceService, RetrievalMode } from '../../../../services/retrieval-mode-preference/retrieval-mode-preference.service';
import {
  isRegulationsAgent,
  normalizeRegulationsCitation,
  parseRegulationsStreamMetadata
} from '../../services/regulation-citation/regulation-citation.util';

/**
 * Agent Chat Container Component
 * 
 * Main container for agent chat functionality. Manages:
 * - Agent chat history and state
 * - API calls for agent interactions (SSE and WebSocket)
 * - Message streaming and updates
 * - Session management
 * - Integration with chatbot service
 * 
 * Features:
 * - Displays agent landing page or chat interface
 * - Handles prompt selection and message sending
 * - Streams responses from agent API
 * - Converts agent chat format to chatbot format
 * - Manages loading states and error handling
 * - Auto-creates sessions for new conversations
 * 
 * Usage:
 * <app-agent-chat-container
 *   [loaderShow]="isLoading"
 *   [userFName]="userName"
 *   [chatbotComponentRef]="chatbotRef"
 *   [isSidebarOpen]="sidebarOpen">
 * </app-agent-chat-container>
 */
@Component({
  selector: 'app-agent-chat-container',
  templateUrl: './agent-chat-container.component.html',
  styleUrls: ['./agent-chat-container.component.scss']
})
export class AgentChatContainerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() loaderShow: boolean = false;
  @Input() userFName: string = '';
  @Input() chatbotComponentRef: ChatbotComponent | null = null;
  @Input() isSidebarOpen: boolean = false;
  @Output() requestCloseSidebar = new EventEmitter<void>();

  @ViewChild('agentChatInterface') agentChatInterface?: AgentChatInterfaceComponent;
  @ViewChild('chatHistoryWrapper', { static: false }) chatHistoryWrapper?: ElementRef;
  @ViewChild(PromptSectionComponent) promptSection?: PromptSectionComponent;
  
  agentId: string = '';
  chatHistory: AgentChatMessage[] = [];
  isNewAgentChat: boolean = true;
  ChatQueriesList: any[] = [];
  promptPills: string[] = [];
  // Next 3 pills: randomly pick one complete group from these 3 groups
  private readonly staticPillGroups = [
    ['United States', 'Canada', 'Thailand'],
    ['Product type: Small molecules, Material type: Drug Product', 'Product type: Small molecules, Material type: Drug Substance', 'Not sure, awaiting confirmation'],
    ['Yes, valid EU GMP certificate', 'No, certificate not yet valid', 'Not sure, awaiting confirmation']
  ];
  
  showScrollToBottomArrow: boolean = false;
  internalLoaderShow: boolean = false;
  isResponseStreaming: boolean = false; // Track if response is actively streaming
  isMetadataLoading: boolean = false;
  isConversationSaved: boolean = true; // Track if conversation_saved event has fired (for disabling follow-up questions)
  isFollowUpQuestionsReceived: boolean = false; // Track if follow_up_q event has been received (for showing dots loader in regulations agent)

  // Share popup state (agent)
  sharePopupData: SharePopupData | null = null;
  shareLinkCopied: boolean = false;

  // Feedback error popup state (shown when save_response_feedback API fails)
  feedbackErrorVisible: boolean = false;
  feedbackErrorText: string = '';

  // Feedback success toast state (shown when save_response_feedback API succeeds)
  feedbackSuccessVisible: boolean = false;
  private feedbackSuccessTimer: ReturnType<typeof setTimeout> | null = null;

  // Download toast state (table + file attachment), assigned in the constructor.
  private readonly tableDownloadSuccessToast: AutoHideToast;
  private readonly tableDownloadErrorToast: AutoHideToast;
  private readonly fileDownloadSuccessToast: AutoHideToast;
  private readonly fileDownloadErrorToast: AutoHideToast;
  private readonly filePreviewErrorToast: AutoHideToast;

  get tableDownloadSuccessVisible(): boolean { return this.tableDownloadSuccessToast.visible; }
  get tableDownloadSuccessFileName(): string { return this.tableDownloadSuccessToast.fileName; }
  get tableDownloadErrorVisible(): boolean { return this.tableDownloadErrorToast.visible; }
  get fileDownloadSuccessVisible(): boolean { return this.fileDownloadSuccessToast.visible; }
  get fileDownloadSuccessFileName(): string { return this.fileDownloadSuccessToast.fileName; }
  get fileDownloadErrorVisible(): boolean { return this.fileDownloadErrorToast.visible; }
  get fileDownloadErrorFileName(): string { return this.fileDownloadErrorToast.fileName; }
  get filePreviewErrorVisible(): boolean { return this.filePreviewErrorToast.visible; }
  get filePreviewErrorFileName(): string { return this.filePreviewErrorToast.fileName; }

  // Document preview modal state
  isDocumentPreviewOpen = false;
  documentPreviewRequest: AgentFilePreviewRequest | null = null;

  // Lazy loading state
  isLoadingMoreHistory: boolean = false;
  hasMoreHistoryPages: boolean = false;
  private isLazyLoadingMore: boolean = false;

  /** When true, the last message (question + answer block) uses slide-up animation. Set only when user just asked in this session, not when switching threads. */
  animateLastMessage: boolean = false;
  private previousHistoryLength: number = 0;
  private previousThreadId: string | null = null;
  /** Set when history was cleared for a session switch (so next loaded history should not animate). */
  private historyClearedForSessionSwitch: boolean = false;

  /** True while loading a session from sidebar (show "Loading session..." overlay). */
  isLoadingSession: boolean = false;

  /** Non-regulations: true from content-complete chunk (text non-empty, is_complete=true) until final chunk (text="", is_complete=true) – show loader below response. */
  isBubblesLoading: boolean = false;

  /** Non-regulations: true only after final chunk (text="" and is_complete=true) received; until then bubbles are disabled. */
  streamFullyComplete: boolean = true;

  /** Source panel (view all sources) – regulation agent only */
  isSourcePanelVisible: boolean = false;
  currentSourceData: SourceContext | null = null;
  /** Cached config for source panel; set when opening panel to avoid new object every CD and repeated API calls */
  sourcePanelConfigData: { FusionAppUIUrl?: string; DomainId?: string; module_name?: string; DownloadApiToggle?: boolean; RIApiUrl?: string; DocChatUrl?: string } | null = null;

  private readonly subscriptions: Subscription[] = [];
  private streamingSubscription: Subscription | null = null;
  private readonly chatbotService = inject(ChatbotService);
  private readonly singleShareConversationService = inject(SingleShareConversationService);
  private currentAgentThreadId: string = '';
  private currentAgentSessionName: string = '';
  private sessionCreated: boolean = false; // Flag to prevent duplicate session creation
  /** Timeouts for session-switch scroll-to-bottom; cleared when user selects another thread so old scrolls don't run. */
  private sessionSwitchScrollTimeouts: ReturnType<typeof setTimeout>[] = [];
  /** Timeouts for scroll-to-bottom when history is updated (e.g. thread selected from sidebar); cleared on next history update. */
  private historyUpdateScrollTimeouts: ReturnType<typeof setTimeout>[] = [];
  

  // SSE streaming state
  private sseBuffer: string = '';
  private sseFullMessageText: string = '';
  private sseLatestThreadId: string = '';
  private sseImageVisualization: string = '';
  private ssePills: string[] = [];
  /** Via agent: bubbles from bubble_item events (shown with pills above prompt). */
  private sseBubbles: string[] = [];
  private sseErrorMessage: string = '';
  private sseMetadata: any[] = [];
  private sseResponseId: string | undefined;
  private sseCurrentQuestion: string = '';
  private sseSaveTriggered: boolean = false;
  private sseCompletionUpdatedAt: string | undefined;

  /** RIA: batch streaming UI updates to avoid re-rendering on every SSE token. */
  private static readonly RIA_STREAM_UI_MIN_INTERVAL_MS = 80;
  private riaStreamFlushRafId: number | null = null;
  private riaStreamFlushTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private riaStreamLastFlushAt = 0;

  // Legacy reasoning state (keep for backward compatibility)
  reasoningSteps: EngagingLoaderStep[] = [];
  showReasoning: boolean = false;
  activeReasoningIndex: number = 0;
  private reasoningAnimationInterval: any = null;
  
  // Engaging Loader state
  showEngagingLoader: boolean = false;
  engagingLoaderIsStreaming: boolean = false;
  engagingLoaderIsCompleted: boolean = false;
  engagingLoaderSteps: EngagingLoaderStep[] = [];
  engagingLoaderCurrentAction: string = '';

  /**
   * Check if agent configuration is available for current agent
   * Used to conditionally show/hide prompt section and disclaimer
   * 
   * @returns boolean - True if config exists for current agent
   */
  get hasAgentConfig(): boolean {
    try {
      if (!this.agentId) {
        return false;
      }
      const config = this.agentConfigService.getAgentConfig(this.agentId);
      return config !== null && config !== undefined;
    } catch (error: any) {
      return false;
    }
  }

  /**
   * Datasets to pass to Prompt Library. Uses chatbot ref when available;
   * for Regulations Agent with no ref/empty list, returns a default Regulations dataset so RegRadar API can run.
   */
  get promptLibraryVectorDbDatasets(): any[] {
    const fromChatbot = this.chatbotComponentRef?.vectorDbDatasets;
    if (fromChatbot && fromChatbot.length > 0) {
      return fromChatbot;
    }
    if (this.agentId === 'regulations_agent') {
      const cfg = this.agentConfigService.getAgentConfig(this.agentId);
      const name = (cfg as any)?.module_display_name || 'Regulations';
      const value = (cfg as any)?.module_name || 'regulations';
      return [{ name, value }];
    }
    return [];
  }

  /**
   * Selected dataset for Prompt Library. Uses chatbot ref when available;
   * for Regulations Agent with no ref, uses default Regulations dataset.
   */
  get promptLibrarySelectedVectorDbDataset(): any {
    if (this.chatbotComponentRef?.selectedVectorDbDataset) {
      return this.chatbotComponentRef.selectedVectorDbDataset;
    }
    const datasets = this.promptLibraryVectorDbDatasets;
    return datasets.length > 0 ? datasets[0] : undefined;
  }

  /**
   * User ID for Prompt Library / RegRadar API. Uses chatbot ref when available; otherwise environment.
   */
  get promptLibraryUserId(): string {
    return (this.chatbotComponentRef?.UserID != null && this.chatbotComponentRef?.UserID !== '')
      ? String(this.chatbotComponentRef.UserID)
      : (environment.LoginUserID ?? '');
  }

  /**
   * Tenant ID for Prompt Library / RegRadar API. Uses chatbot ref when available; otherwise environment.
   */
  get promptLibraryTenantId(): string {
    const fromChatbot = this.chatbotComponentRef?.tenantID ?? this.chatbotComponentRef?.configdata?.default_tenant_id;
    return (fromChatbot != null && fromChatbot !== '')
      ? String(fromChatbot)
      : (environment.TenantId ?? '');
  }

  /**
   * Domain name for Prompt Library / RegRadar API. Uses chatbot ref when available; otherwise environment.
   */
  get promptLibraryDomainName(): string {
    const fromChatbot = this.chatbotComponentRef?.moduleName;
    return (fromChatbot != null && fromChatbot !== '')
      ? String(fromChatbot)
      : (environment.DomainName ?? '');
  }

  /** Domain names for @-mentions in prompt section when chatbot list is empty (e.g. Regulations Agent). */
  promptSectionDomainNames: string[] = [];

  /** Domains the user selected via @-mentions in the current prompt text. */
  userSelectedDomains: string[] = [];

  /**
   * Domain names to pass to prompt-section: from chatbot when available and non-empty, else agent-fetched list.
   */
  get promptSectionAllDomainNames(): string[] {
    const fromChatbot = this.chatbotComponentRef?.allDomainNamesList;
    if (fromChatbot && Array.isArray(fromChatbot) && fromChatbot.length > 0) {
      return fromChatbot;
    }
    return this.promptSectionDomainNames;
  }

  /** Whether domain management is enabled (resolved from ConfigurationService feature flag for regulations_agent). */
  promptSectionIsDomainManagementEnabled: boolean = false;

  /** Whether retrieval mode toggle is enabled for regulations_agent. */
  isRetrievalModeFeatureEnabled: boolean = false;

  /**
   * Minimal config for chatbot-metadata (sources panel): FusionAppUIUrl, DomainId, module_name.
   * Used when displaying regulation/RTQ metadata in agent chat.
   */
  get metadataConfigData(): { FusionAppUIUrl?: string; DomainId?: string; module_name?: string; DownloadApiToggle?: boolean; RIApiUrl?: string; DocChatUrl?: string } {
    const agentConfig = this.agentId ? this.agentConfigService.getAgentConfig(this.agentId) : undefined;
    const regulationsModule = (typeof moduleInfo !== 'undefined' && (moduleInfo as any)?.view_access_to_regulations) || {};
    return {
      FusionAppUIUrl: (config as any)?.FusionAppUIUrl ?? '',
      DomainId: this.sourcePanelDomainId,
      module_name: agentConfig?.module_name,
      DownloadApiToggle: regulationsModule.DownloadApiToggle ?? (config as any)?.DownloadApiToggle,
      RIApiUrl: regulationsModule.RIApiUrl ?? (config as any)?.RIApiUrl,
      DocChatUrl: regulationsModule.DocChatUrl ?? (config as any)?.DocChatUrl
    };
  }

  /**
   * Tenant ID for source panel (view all sources). Uses chatbot ref when available; otherwise environment/config so the panel can call search_metadata.
   */
  get sourcePanelTenantId(): string {
    return (environment.TenantId ?? this.chatbotComponentRef?.tenantID ?? '') || '';
  }

  /**
   * Domain ID for source panel. Uses chatbot ref when available; otherwise default.
   */
  get sourcePanelDomainId(): string {
    return (this.chatbotComponentRef?.DomainID ?? (config as any)?.DomainName ?? '') || '';
  }

  /**
   * selected_module_name for chatbot-metadata: maps agent module_name to chatbot module name.
   * Only regulations and rtq agents show metadata in the UI.
   */
  get metadataModuleName(): string {
    const agentConfig = this.agentId ? this.agentConfigService.getAgentConfig(this.agentId) : undefined;
    const name = agentConfig?.module_name?.toLowerCase();
    if (name === 'regulations') return 'view_access_to_regulations';
    if (name === 'rtq') return 'view_access_to_rtq';
    return '';
  }

  /** True when the current agent should show follow-up bubbles/pills above the prompt (from agent_config.options.showBubbles). */
  get showBubblesForCurrentAgent(): boolean {
    const opts = this.agentId ? this.agentConfigService.getAgentConfig(this.agentId)?.options : undefined;
    return opts?.showBubbles !== false;
  }

  constructor(
    public agentOrchestrator: AgentOrchestratorService,
    private readonly agentConfigService: AgentConfigService,
    private readonly cdr: ChangeDetectorRef,
    private readonly logger: LoggerService,

    private readonly streamingApiService: StreamingApiServicesService,
    private readonly streamingStateService: StreamingStateService,
    private readonly getAgentResponseHistoryService: GetAgentResponseHistoryService,
    private readonly configurationService: ConfigurationService,
    private readonly agentApiService: AgentApiService,
    private readonly apiService: ApiService,
    private readonly tokenService: TokenService,
    private readonly chatAutoScrollService: ChatAutoScrollService,
    private readonly agentPromptPrefixService: AgentPromptPrefixService,
    private readonly saveAgentResponseService: SaveAgentResponseService,
    private readonly translate: TranslateService,
    private readonly retrievalModePreference: RetrievalModePreferenceService
  ) {
    this.tableDownloadSuccessToast = new AutoHideToast(this.cdr, 6000);
    this.tableDownloadErrorToast = new AutoHideToast(this.cdr, 4000);
    this.fileDownloadSuccessToast = new AutoHideToast(this.cdr, 6000);
    this.fileDownloadErrorToast = new AutoHideToast(this.cdr, 6000);
    this.filePreviewErrorToast = new AutoHideToast(this.cdr, 6000);
  }

  /**
   * Initialize component and setup subscriptions
   */
  ngOnInit(): void {
    this.agentConfigService.loadAgentConfig().then(() => this.initializeSubscriptions()).catch((error: unknown) => {
      this.logger.logInfo('[AgentChatContainer] Error in ngOnInit', 'ngOnInit', error instanceof Error ? error.message : String(error));
    });
  }

  /**
   * Setup orchestrator and related subscriptions (runs after config is loaded).
   */
  private initializeSubscriptions(): void {
    try {
      this.subscriptions.push(
        this.agentOrchestrator.currentAgentId$.subscribe((agentId: string) => {
          const previousAgentId = this.agentId;
          this.agentId = agentId;
          if (previousAgentId && previousAgentId !== agentId) {
            this.stopAllAudioIfPresent();

            // Reset streaming/loader state so the new agent starts clean
            if (this.streamingSubscription) {
              this.streamingSubscription.unsubscribe();
              this.streamingSubscription = null;
            }
            this.streamingStateService.setStreamingState(false);
            this.internalLoaderShow = false;
            this.isResponseStreaming = false;
            this.isConversationSaved = true;
            this.showEngagingLoader = false;
            this.engagingLoaderIsStreaming = false;
            this.engagingLoaderIsCompleted = false;
            this.engagingLoaderSteps = [];
            this.engagingLoaderCurrentAction = '';
            this.feedbackErrorVisible = false;
            this.feedbackErrorText = '';
            this.feedbackSuccessVisible = false;
          }
          // Clear prompt input and domain selections whenever agent changes
          this.clearPromptInput();
          this.userSelectedDomains = [];
          // Resolve domain management feature flag for regulations agent
          if (agentId === 'regulations_agent') {
            this.configurationService.isDomainManagementEnabled().then(enabled => {
              this.promptSectionIsDomainManagementEnabled = enabled;
              this.cdr.markForCheck();
            });
            this.configurationService.isRetrievalModeEnabled().then(enabled => {
              this.isRetrievalModeFeatureEnabled = enabled;
              this.cdr.markForCheck();
            });
          } else {
            this.promptSectionIsDomainManagementEnabled = false;
            this.isRetrievalModeFeatureEnabled = false;
            this.promptSectionDomainNames = [];
          }
        })
      );

      // Clear prompt when new/existing chat state toggles (e.g., selecting history sets false)
      this.subscriptions.push(
        this.agentOrchestrator.isNewAgentChat$.subscribe(() => {
          this.clearPromptInput();
        })
      );

      this.subscriptions.push(
        this.agentOrchestrator.agentChatHistory$.subscribe(history => {
          this.chatHistory = history;

          // Only animate last message when user just added it in this session; not when switching threads.
          const currentThreadId = this.agentOrchestrator.getCurrentThreadId();
          const threadChanged = currentThreadId !== this.previousThreadId;
          // When session/thread changed (header or sidebar), clear pills so previous session's bubbles don't show
          if (threadChanged) {
            this.ssePills = [];
            this.sseBubbles = [];
            this.promptPills = [];
          }
          // The sidebar session switch loads history without passing through stopStreaming(), so the
          // previous thread's live steps would win over the loaded answer's persisted thinking.
          if (history.length > 0 && this.agentOrchestrator.getSessionLoadInProgress()) {
            this.clearLiveEngagingLoader();
          }
          const historyGrew = history.length > this.previousHistoryLength;
          if (history.length === 0) {
            this.animateLastMessage = false;
            if (this.previousThreadId != null) {
              this.historyClearedForSessionSwitch = true; // Next history load is from switch, don't animate
            }
            this.previousThreadId = null;
          } else {
            if (this.agentOrchestrator.getSessionLoadInProgress()) {
              this.animateLastMessage = false;
              this.agentOrchestrator.setSessionLoadInProgress(false);
            } else if (this.historyClearedForSessionSwitch) {
              this.animateLastMessage = false;
              this.historyClearedForSessionSwitch = false;
            } else if (historyGrew) {
              // User just asked in this session: same thread, or first question (previousThreadId was null)
              this.animateLastMessage = !threadChanged || this.previousThreadId === null;
            } else if (threadChanged) {
              this.animateLastMessage = false;
            }
          }
          // Only update previousThreadId when we have history so "New chat" reset (previousThreadId = null) is not overwritten by stale orchestrator thread id.
          if (history.length > 0) {
            this.previousThreadId = currentThreadId ?? null;
          }
          this.previousHistoryLength = history.length;

          if (this.canPatchLastChatQueryDuringRiaStream(history)) {
            this.patchLastChatQueryFromStreamingHistory(history[history.length - 1]);
          } else {
            // Force array reference change for Angular change detection
            const newChatQueriesList = this.convertAgentChatToChatQueriesList(history);
            this.ChatQueriesList = [...newChatQueriesList];
          }

          if (history.length > 0) {
            const latestMessage = history[history.length - 1];
            this.internalLoaderShow = latestMessage.isLoading || false;
            // When history is from API (no message loading), enable Related Questions
            if (!latestMessage.isLoading) {
              this.isConversationSaved = true;
              this.isResponseStreaming = false;
              if (this.agentId !== 'regulations_agent') {
                this.streamFullyComplete = true; // Loaded session: bubbles clickable
              }
            }
            if (this.chatbotComponentRef) {
              if (this.internalLoaderShow) {
                this.chatbotComponentRef.showLoader();
              } else {
                this.chatbotComponentRef.hideLoader();
              }
            }
          } else {
            // History cleared (e.g. after delete session or new chat) – enable send button
            this.internalLoaderShow = false;
            this.isResponseStreaming = false;
            this.isConversationSaved = true;
            if (this.chatbotComponentRef) {
              this.chatbotComponentRef.hideLoader();
            }
          }
          
          // Lightweight RIA stream patches are followed by updateSSEUI detectChanges; skip duplicate pass here.
          if (!this.canPatchLastChatQueryDuringRiaStream(history)) {
            this.cdr.detectChanges();
          }

          // When history is set (e.g. thread selected from sidebar or header), scroll to last message.
          // Only run when NOT streaming so we don't reset userHasScrolledUp during SSE (keeps interrupt working).
          // Use same multi-delay 'auto' scroll as executeSessionSwitch so slow-render threads still scroll.
          if (
            !this.isLazyLoadingMore &&
            history.length > 0 &&
            !this.internalLoaderShow &&
            !this.isResponseStreaming
          ) {
            this.historyUpdateScrollTimeouts.forEach(t => clearTimeout(t));
            this.historyUpdateScrollTimeouts = [];
            // Do not reset when user deliberately scrolled up — resetScrollState clears userHasScrolledUp
            // and would cause an unwanted jump to bottom after streaming completes.
            if (!this.chatAutoScrollService.getScrollState().userHasScrolledUp) {
              this.chatAutoScrollService.resetScrollState();
            }
            const scrollTargetFn = (): { nativeElement: HTMLElement } | undefined => {
              const ref = this.agentChatInterface?.scrollTarget;
              if (ref) return ref;
              const el = document.getElementById('agent-chat-scrolltobottom');
              return el ? { nativeElement: el } : undefined;
            };
            const doScroll = (): void => {
              if (this.chatAutoScrollService.getScrollState().userHasScrolledUp) {
                return;
              }
              const scrollTarget = scrollTargetFn();
              this.chatAutoScrollService.findChatContainer(undefined, scrollTarget ?? undefined);
              this.chatAutoScrollService.scrollToBottom('auto');
            };
            const delays = [0, 100, 350, 600, 1000, 1400, 2000, 2800];
            delays.forEach(delayMs => {
              this.historyUpdateScrollTimeouts.push(setTimeout(doScroll, delayMs));
            });
          }
        })
      );

      this.subscriptions.push(
        this.agentOrchestrator.isLoadingSession$.subscribe(loading => {
          this.isLoadingSession = loading;
          this.cdr.detectChanges();
        })
      );

      this.subscriptions.push(
        this.agentOrchestrator.isNewAgentChat$.subscribe(isNew => {
          this.isNewAgentChat = isNew;
          if (isNew) {
            this.stopAllAudioIfPresent();
            // Reset session created flag when starting new chat
            this.sessionCreated = false;
            this.currentAgentThreadId = '';
            this.currentAgentSessionName = '';
          }
        })
      );

      // Subscribe to lazy load state changes
      this.subscriptions.push(
        this.agentOrchestrator.lazyLoadState$.subscribe(state => {
          this.isLoadingMoreHistory = state.isLoading;
          this.hasMoreHistoryPages = state.hasMore;
        })
      );

      // Register stopStreaming callback for orchestrator to call during confirmations
      this.agentOrchestrator.registerStopStreamingCallback(() => this.stopStreaming());
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error in ngOnInit', 'ngOnInit', error);
    }
  }

  /**
   * Update scroll arrow visibility
   * Prevents ExpressionChangedAfterItHasBeenCheckedError
   * 
   * @param show - Whether to show scroll arrow
   */
  onScrollArrowChange(show: boolean): void {
    try {
      if (this.showScrollToBottomArrow !== show) {
        this.showScrollToBottomArrow = show;
        this.cdr.detectChanges();
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error updating scroll arrow', 'onScrollArrowChange', error);
    }
  }

  /**
   * When audio-copy updates list (e.g. isSpeaking, isPaused), sync to container list.
   */
  onChatQueriesListEmit(data: any[]): void {
    try {
      if (!Array.isArray(data)) return;
      this.ChatQueriesList.forEach((chat: any, index: number) => {
        if (data[index]) {
          chat.isSpeaking = data[index].isSpeaking;
          chat.isPaused = data[index].isPaused;
        }
      });
      this.cdr.detectChanges();
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error in onChatQueriesListEmit', 'onChatQueriesListEmit', error);
    }
  }

  /**
   * Called when user scrolls up; cancels all pending scroll-to-bottom timeouts so the view is not pulled back to bottom.
   */
  onUserScrolledUp(): void {
    this.historyUpdateScrollTimeouts.forEach(t => clearTimeout(t));
    this.historyUpdateScrollTimeouts = [];
    this.sessionSwitchScrollTimeouts.forEach(t => clearTimeout(t));
    this.sessionSwitchScrollTimeouts = [];
  }

  /**
   * Handle scroll to bottom button click
   */
  scrollToBottomClick(): void {
    try {
      if (this.agentChatInterface) {
        this.agentChatInterface.scrollToBottomClick();
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error scrolling to bottom', 'scrollToBottomClick', error);
    }
  }

  /**
   * Handle new chat request from header dropdown
   * Similar to chatbot's ClickNewChat - checks streaming state before proceeding
   */
  onAgentNewChat(): void {
    try {
      // Check if streaming is active (similar to chatbot logic)
      if (this.streamingSubscription || this.internalLoaderShow || this.streamingStateService.getCurrentStreamingState()) {
        this.logger.logInfo('[AgentChatContainer] Streaming active - showing confirmation popup', 'onAgentNewChat');
        this.agentOrchestrator.checkStreamingAndExecute(() => {
          this.executeNewChat();
        }, 'new_chat');
        return;
      }

      // Execute immediately if not streaming
      this.executeNewChat();
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error starting new chat', 'onAgentNewChat', error);
    }
  }

  /**
   * Stop streaming subscription – same as chatbot stopStreaming (close WebSockets + unsubscribe
   * so the API stops sending chunks). Order: close WebSocket first so connection drops, then unsubscribe.
   */
  public stopStreaming(): void {
    try {
      // 1. Close WebSocket connections first (same condition as chatbot) – so regulations agent
      //    stream (callChunkedApiV2) stops on the wire and responseSubject completes
      if (
        this.chatbotService.AllWebSocketsConnectionsOpen() ||
        this.chatbotService.isWebSocketOpen() ||
        this.chatbotService.isNewsWebSocketOpen() ||
        this.chatbotService.isFileStatusTrackerWebSocketOpen()
      ) {
        this.chatbotService.closeAllWebSockets();
        this.logger.logInfo('[AgentChatContainer] WebSockets closed (stopStreaming)', 'stopStreaming');
      }

      // 2. Unsubscribe from streaming (same as chatbot)
      if (this.streamingSubscription) {
        this.streamingSubscription.unsubscribe();
        this.streamingSubscription = null;
        this.logger.logInfo('[AgentChatContainer] Streaming subscription stopped', 'stopStreaming');
      }

      // 3. Reset streaming state and clear loaders
      this.streamingStateService.setStreamingState(false);
      this.internalLoaderShow = false;
      this.isResponseStreaming = false;
      this.isMetadataLoading = false;
      
      // Reset engaging loader state
      this.showEngagingLoader = false;
      this.engagingLoaderIsStreaming = false;
      this.engagingLoaderIsCompleted = false;
      this.engagingLoaderSteps = [];
      this.engagingLoaderCurrentAction = '';
      
      // Clear streaming data to prevent old responses from rendering
      this.sseBuffer = '';
      this.sseFullMessageText = '';
      this.sseLatestThreadId = '';
      this.sseImageVisualization = '';
      this.ssePills = [];
      this.sseBubbles = [];
      this.sseErrorMessage = '';
      this.sseMetadata = [];
      this.isBubblesLoading = false;
      if (this.agentId !== 'regulations_agent') {
        this.streamFullyComplete = true; // After stop, allow any remaining UI to be clickable
      }
      this.cancelRiaStreamUiFlush();
    } catch (error: any) {
      this.logger.logError(`Error stopping streaming: ${error}`, 'AgentChatContainerComponent.stopStreaming');
    }
  }

  /**
   * Execute new chat logic (separated for reuse in confirmation)
   */
  private executeNewChat(): void {
    try {
      if (this.isNewAgentChat) {
        return;
      }
      
      // Stop any active streaming first
      this.stopStreaming();
      
      // Clear agent session state (similar to chatbot's ClickNewChat)
      this.currentAgentThreadId = '';
      this.currentAgentSessionName = '';
      this.sessionCreated = false;
      
      // Clear reasoning states for new chat
      this.reasoningSteps = [];
      this.showReasoning = false;
      this.activeReasoningIndex = 0;
      
      // Clear pills above prompt section for new chat
      this.promptPills = [];
      
      // Reset engaging loader state
      this.showEngagingLoader = false;
      this.engagingLoaderIsStreaming = false;
      this.engagingLoaderIsCompleted = false;
      this.engagingLoaderSteps = [];
      this.engagingLoaderCurrentAction = '';
      
      // Clear chat history and reset orchestrator state
      this.agentOrchestrator.clearAgentChatHistory();
      this.agentOrchestrator.resetAgentState();

      // New chat: no streaming, enable state so any future pills aren’t stuck disabled
      this.isConversationSaved = true;

      // Ensure the agent prompt input and domain selections are cleared
      this.clearPromptInput();
      this.userSelectedDomains = [];
      
      this.logger.logInfo('[AgentChatContainer] Agent session state cleared for new chat', 'executeNewChat');
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error executing new chat', 'executeNewChat', error);
    }
  }

  /**
   * Handle about request from header dropdown
   */
  onAgentAbout(): void {
    try {
      this.logger.logInfo(`[AgentChatContainer] About clicked for agent: ${this.agentId}`, 'onAgentAbout');
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error handling about', 'onAgentAbout', error);
    }
  }

  /**
   * Handle session selection from the header dropdown.
   * Checks streaming state and shows confirmation popup if needed,
   * then loads thread history and updates orchestrator so the chat area displays it.
   */
  onSessionSelected(thread: { thread_id: string }): void {
    try {
      this.stopAllAudioIfPresent();
      if (!thread?.thread_id) {
        this.logger.logError('[AgentChatContainer] No thread_id provided', 'onSessionSelected');
        return;
      }

      // Check if streaming is active - show confirmation popup like onAgentNewChat
      if (this.streamingSubscription || this.internalLoaderShow || this.streamingStateService.getCurrentStreamingState()) {
        this.logger.logInfo('[AgentChatContainer] Streaming active - showing confirmation popup', 'onSessionSelected');
        this.agentOrchestrator.checkStreamingAndExecute(() => {
          this.executeSessionSwitch(thread);
        }, 'session_switch');
        return;
      }

      // Execute immediately if not streaming
      this.executeSessionSwitch(thread);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error handling session selection', 'onSessionSelected', error);
    }
  }

  /**
   * Execute session switch logic (separated for reuse in streaming confirmation)
   */
  private executeSessionSwitch(thread: { thread_id: string }): void {
    try {
      // Cancel any pending scroll timeouts from a previous thread so they don't run and overwrite scroll position
      this.sessionSwitchScrollTimeouts.forEach(t => clearTimeout(t));
      this.sessionSwitchScrollTimeouts = [];
      this.historyUpdateScrollTimeouts.forEach(t => clearTimeout(t));
      this.historyUpdateScrollTimeouts = [];
      // Force next scroll to resolve container fresh (avoids stale container after multiple thread switches)
      this.chatAutoScrollService.cleanup(false);
      // Stop any active streaming
      this.stopStreaming();

      const threadId = thread.thread_id;
      const userID = (this.chatbotComponentRef?.UserID) || environment.LoginUserID || '';
      const tenantID = environment.TenantId || '';

      this.agentOrchestrator.setLoadingSession(true);
      this.agentOrchestrator.updateLazyLoadState(true, true);
      this.agentOrchestrator.setCurrentThreadId(threadId);
      this.agentOrchestrator.setIsNewChat(false);

      // Clear previous session bubbles/messages immediately so they don't show while loading
      this.agentOrchestrator.setSessionLoadInProgress(true); // No animation when loaded history arrives
      this.agentOrchestrator.updateChatHistory([]);
      // Clear Via (and any agent) bubbles/pills: SSE state and prompt pills so previous session pills don't persist
      this.ssePills = [];
      this.sseBubbles = [];
      this.promptPills = []; // new reference so template updates
      this.isBubblesLoading = false;
      this.streamFullyComplete = true; // Will be set from loaded history or next stream's final chunk
      this.currentAgentThreadId = threadId;
      this.cdr.markForCheck();
      this.cdr.detectChanges();

      const request: GetAgentResponseHistoryRequest = {
        thread_id: threadId,
        user_id: userID,
        tenant_id: tenantID,
        page: 1,
        page_size: 5
      };

      const sub = this.getAgentResponseHistoryService.getResponseHistory(request, this.agentId).subscribe({
        next: (response) => {
          // Only apply if still on this thread (avoid previous session's last Q&A overwriting new session)
          if (this.agentOrchestrator.getCurrentThreadId() !== threadId) {
            this.agentOrchestrator.setLoadingSession(false);
            this.agentOrchestrator.updateLazyLoadState(false, false);
            return;
          }
          if (response.status === 'success' && response.data) {
            const historyMessages = (response.data.responses || [])
              .map((resp: any) =>
                mapHistoryResponseToAgentMessage(
                  resp,
                  resp.image_visualization || this.extractImageVisualizationFromResponse(resp)
                )
              )
              .reverse();

            const applyHistory = (resolvedMessages: AgentChatMessage[]): void => {
              if (this.agentOrchestrator.getCurrentThreadId() !== threadId) {
                this.agentOrchestrator.setLoadingSession(false);
                this.agentOrchestrator.updateLazyLoadState(false, false);
                return;
              }

              if (resolvedMessages.length === 0) {
                this.agentOrchestrator.setIsNewChat(true);
                this.agentOrchestrator.showAgentLandingPage();
              } else {
                this.isConversationSaved = true;
                this.isResponseStreaming = false;
                this.internalLoaderShow = false;
                this.agentOrchestrator.updateChatHistory(resolvedMessages);
                this.agentOrchestrator.hideAgentLanding();
              }
              const totalPages = response.data!.pagination?.total_pages ?? 1;
              const currentPage = response.data!.pagination?.current_page ?? 1;
              this.agentOrchestrator.updateHistoryPagination(currentPage, totalPages);
              this.agentOrchestrator.updateLazyLoadState(false, currentPage < totalPages);
              this.cdr.detectChanges();

              if (resolvedMessages.length > 0) {
                this.chatAutoScrollService.resetScrollState();
                const scrollTargetFn = (): { nativeElement: HTMLElement } | undefined => {
                  const ref = this.agentChatInterface?.scrollTarget;
                  if (ref) return ref;
                  const el = document.getElementById('agent-chat-scrolltobottom');
                  return el ? { nativeElement: el } : undefined;
                };
                const doScroll = (behavior: 'auto' | 'smooth', _delayMs: number): void => {
                  const currentThreadId = this.agentOrchestrator.getCurrentThreadId?.() ?? '';
                  if (currentThreadId !== threadId) {
                    return;
                  }
                  if (this.chatAutoScrollService.getScrollState().userHasScrolledUp) {
                    return;
                  }
                  const scrollTarget = scrollTargetFn();
                  this.chatAutoScrollService.findChatContainer(undefined, scrollTarget ?? undefined);
                  this.chatAutoScrollService.scrollToBottom(behavior);
                };
                this.sessionSwitchScrollTimeouts.push(setTimeout(() => doScroll('auto', 0), 0));
                this.sessionSwitchScrollTimeouts.push(setTimeout(() => doScroll('auto', 100), 100));
                this.sessionSwitchScrollTimeouts.push(setTimeout(() => doScroll('auto', 350), 350));
                this.sessionSwitchScrollTimeouts.push(setTimeout(() => doScroll('auto', 600), 600));
                this.sessionSwitchScrollTimeouts.push(setTimeout(() => doScroll('auto', 1000), 1000));
                this.sessionSwitchScrollTimeouts.push(setTimeout(() => doScroll('auto', 1400), 1400));
                this.sessionSwitchScrollTimeouts.push(setTimeout(() => doScroll('auto', 2000), 2000));
                this.sessionSwitchScrollTimeouts.push(setTimeout(() => doScroll('auto', 2800), 2800));
              }
              this.agentOrchestrator.setLoadingSession(false);
            };

            applyHistory(historyMessages);
          } else {
            // Non-success (e.g. validation error) – clear lazy load spinner
            this.logger.logError(`[AgentChatContainer] Response history returned non-success: ${response.message || response.status}`, 'executeSessionSwitch');
            this.agentOrchestrator.updateLazyLoadState(false, false);
            this.agentOrchestrator.setSessionLoadInProgress(false);
            this.agentOrchestrator.setLoadingSession(false);
          }
        },
        error: (err) => {
          this.logger.logError(`[AgentChatContainer] Failed to load thread history: ${err?.message || err}`, 'onSessionSelected');
          this.agentOrchestrator.updateLazyLoadState(false, false);
          this.agentOrchestrator.setSessionLoadInProgress(false);
          this.agentOrchestrator.setLoadingSession(false);
        }
      });

      this.subscriptions.push(sub);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error executing session switch', 'executeSessionSwitch', error);
    }
  }

  /**
   * Clear the agent prompt input if available
   */
  private clearPromptInput(): void {
    try {
      if (this.promptSection) {
        this.promptSection.clearInput();
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error clearing prompt input', 'clearPromptInput', error);
    }
  }

  /**
   * Returns the user-selected domains (from dropdown selections) or an empty array.
   */
  private getSelectedDomains(): string[] {
    if (this.userSelectedDomains && Array.isArray(this.userSelectedDomains) && this.userSelectedDomains.length > 0) {
      return this.userSelectedDomains;
    }
    return [];
  }

  /**
   * Returns all available domains for the current agent or an empty array.
   */
  private getAvailableDomains(): string[] {
    const domains = this.promptSectionAllDomainNames;
    if (domains && Array.isArray(domains) && domains.length > 0) {
      return domains;
    }
    return [];
  }

  /**
   * When user opens the sidebar (collapse open), close the view-all-sources panel – same as chatbot.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const sidebarChange = changes['isSidebarOpen'];
    if (sidebarChange && sidebarChange.currentValue === true && sidebarChange.previousValue === false) {
      if (this.isSourcePanelVisible) {
        this.closeSourcePanel();
      }
    }
  }

  /**
   * Cleanup subscriptions on destroy
   */
  ngOnDestroy(): void {
    try {
      this.stopAllAudioIfPresent();
      this.sessionSwitchScrollTimeouts.forEach(t => clearTimeout(t));
      this.sessionSwitchScrollTimeouts = [];
      this.historyUpdateScrollTimeouts.forEach(t => clearTimeout(t));
      this.historyUpdateScrollTimeouts = [];
      this.cancelRiaStreamUiFlush();
      this.subscriptions.forEach(sub => sub.unsubscribe());
      if (this.streamingSubscription) {
        this.streamingSubscription.unsubscribe();
      }
      
      // Cleanup callback registration
      this.agentOrchestrator.registerStopStreamingCallback(null);

      this.tableDownloadSuccessToast.dispose();
      this.tableDownloadErrorToast.dispose();
      this.fileDownloadSuccessToast.dispose();
      this.fileDownloadErrorToast.dispose();
      this.filePreviewErrorToast.dispose();
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error in ngOnDestroy', 'ngOnDestroy', error);
    }
  }

  /**
   * Stop all audio playback when switching thread, agent, or leaving (hub).
   */
  private stopAllAudioIfPresent(): void {
    try {
      if (this.agentChatInterface?.stopAllAudio) {
        this.agentChatInterface.stopAllAudio();
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error stopping audio', 'stopAllAudioIfPresent', error);
    }
  }

  /**
   * Show share popup (from share-conversation component).
   *
   * Delegates to SingleShareConversationService which dynamically checks
   * if the current agent has a `share_conversation` endpoint configured
   * in api_endpoints.json.
   *
   * This allows any agent to support single-response sharing simply by
   * adding a `share_conversation` endpoint to its config block.
   */
  onShowSharePopup(data: any): void {
    this.shareLinkCopied = false;

    const chatdata = data?.chatdata;
    const responseId = chatdata?.pk_chathistoryid;
    const tenantId = environment.TenantId || '';
    const userId = (this.chatbotComponentRef?.UserID) || environment.LoginUserID || '';
    const threadId = this.getCurrentAgentThreadId() || '';

    this.singleShareConversationService
      .getSharePopupData(this.agentId, tenantId, responseId, userId, threadId)
      .subscribe({
        next: (popupData) => {
          this.sharePopupData = popupData;
          this.cdr.detectChanges();
        },
        error: () => {
          this.sharePopupData = { share_link: null, placeholder: true };
          this.cdr.detectChanges();
        }
      });
  }

  /** Hide share popup. */
  onHideSharePopup(): void {
    this.sharePopupData = null;
    this.shareLinkCopied = false;
    this.cdr.detectChanges();
  }

  /** Close share popup (button in popup). */
  closeSharePopup(): void {
    this.onHideSharePopup();
  }

  /** Handle keydown on share popup (e.g. Escape to close). */
  onSharePopupKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeSharePopup();
    }
  }

  /** Copy share link to clipboard (when link is present). */
  copyShareLink(): void {
    if (!this.sharePopupData?.share_link) return;
    navigator.clipboard
      .writeText(this.sharePopupData.share_link)
      .then(() => {
        this.shareLinkCopied = true;
        this.cdr.detectChanges();

        // Reset after 2 seconds
        setTimeout(() => {
          this.shareLinkCopied = false;
          this.cdr.detectChanges();
        }, 2000);
      })
      .catch((error: unknown) => {
        this.logger.logInfo(
          '[AgentChatContainer] Error copying share link',
          'copyShareLink',
          error instanceof Error ? error.message : String(error)
        );
      });
  }

  /**
   * Convert agent chat format to chatbot queries list format
   * 
   * @param agentChatHistory - Agent chat messages
   * @returns Converted chat queries list
   */
  /**
   * Fallback extractor for image_visualization when transformRegulationsResponse was not applied
   * (e.g. non-regulations agents or edge-case response formats).
   * Handles: JSON-string answers, object answers, top-level arrays and raw strings.
   */
  private extractImageVisualizationFromResponse(resp: any): string {
    try {
      // Try to parse answer as JSON and look for image_visualization inside
      let imgArray: any[] | null = null;
      if (typeof resp.answer === 'string') {
        try {
          const parsed = JSON.parse(resp.answer);
          if (parsed.image_visualization && Array.isArray(parsed.image_visualization)) {
            imgArray = parsed.image_visualization;
          }
        } catch { /* not JSON */ }
      } else if (resp.answer && typeof resp.answer === 'object') {
        if (resp.answer.image_visualization && Array.isArray(resp.answer.image_visualization)) {
          imgArray = resp.answer.image_visualization;
        }
      }

      // Fall back to top-level resp.image_visualization array
      if (!imgArray && resp.image_visualization && Array.isArray(resp.image_visualization)) {
        imgArray = resp.image_visualization;
      }

      if (imgArray && imgArray.length > 0) {
        return imgArray
          .map((img: any) => {
            if (typeof img === 'string') return img.trim();
            return img?.png_base64?.trim() || '';
          })
          .filter((b64: string) => !!b64)
          .map((b64: string) => b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`)
          .join(' ');
      }
      return '';
    } catch {
      return '';
    }
  }

  private getSendTimeRetrievalMode(): RetrievalMode | undefined {
    if (this.agentId !== 'regulations_agent' || !this.isRetrievalModeFeatureEnabled) {
      return undefined;
    }
    return this.retrievalModePreference.getRetrievalMode();
  }

  private convertAgentChatToChatQueriesList(agentChatHistory: AgentChatMessage[]): any[] {
    try {
      const converted = agentChatHistory.map((message, index) => {
          // Create new object for change detection
        const metadata = message.metadata != null
          ? (Array.isArray(message.metadata) ? [...message.metadata] : { ...message.metadata })
          : {};
        // Restore feedback state from history (same as chatbot v2 history load)
        const msgAny = message as any;
        let historyIsLike = msgAny.is_like !== undefined ? msgAny.is_like : null;

        const likeClassAdded = historyIsLike === true;
        const disLikeClassAdded = historyIsLike === false;

        // Restore FeedbackOptions from config, marking previously checked options
        let feedbackOptions: any[] = [];
        if (disLikeClassAdded && msgAny.feedback) {
          const configOptions = (config as any)?.FeedbackOptions || [];
          const savedFeedback = typeof msgAny.feedback === 'string' ? JSON.parse(msgAny.feedback) : msgAny.feedback;
          feedbackOptions = configOptions.map((opt: any) => ({
            ...opt,
            IsChecked: savedFeedback && savedFeedback[String(opt.Id)] ? true : false,
            pk_chathistoryid: (message as any).response_id || (index + 1)
          }));
        }

        const newMessage = {
          pk_chathistoryid: (message as any).response_id != null ? (message as any).response_id : message.response_id || (index + 1),
          response_id: (message as any).response_id,
          question: message.question,
          answer: message.answer,
          LikeClassAdded: likeClassAdded,
          disLikeClassAdded: disLikeClassAdded,
          SubmitFeedback: false,
          metadata,
          followUpQuestions: message.pills || [],
          pills: message.pills || [],
          modified_date: message.updated_at || '',
          isSpeaking: false,
          isPaused: false,
          utterance: null,
          image_visualization: message.image_visualization || '',
          news_feeds_id: null,
          newsAndUpdates: [],
          isLoading: message.isLoading || false,
          FeedbackOptions: feedbackOptions,
          AdditionalComments: msgAny.comment !== undefined ? msgAny.comment : '',
          errorMessage: message.errorMessage,
          completedReasoningSteps: message.completedReasoningSteps || [],
          file_paths: message.file_paths || [],
          retrieval_mode: message.retrieval_mode
        };
        
        return newMessage;
      });
      
      return converted;
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error converting chat history', 'convertAgentChatToChatQueriesList', error);
      return [];
    }
  }

  /**
   * Handle prompt selection from landing page
   * 
   * @param promptText - Selected prompt text
   */
  onPromptSelected(promptText: string): void {
    try {
      const currentThreadId = this.getCurrentAgentThreadId();
      if (!currentThreadId || currentThreadId === '') {
        this.createNewAgentSession(promptText);
      }

      this.agentOrchestrator.addAgentMessage(promptText, this.getSendTimeRetrievalMode());
      this.callAgentApi(promptText);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error handling prompt selection', 'onPromptSelected', error);
    }
  }

  /**
   * Handle send request from prompt section
   * 
   * @param message - User message
   */
  onSendRequest(message: string): void {
    try {
      const currentThreadId = this.getCurrentAgentThreadId();
      if (!currentThreadId || currentThreadId === '') {
        this.createNewAgentSession(message);
      }

      // userSelectedDomains is already populated by onPromptSectionSuggestionSelected
      // when the user picks domains from the @-mention dropdown.
      this.agentOrchestrator.addAgentMessage(message, this.getSendTimeRetrievalMode());
      this.callAgentApi(message);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error sending request', 'onSendRequest', error);
    }
  }

  /**
   * Handle 3-dots menu: Regenerate / Make crisp / Detailed (same as chatbot RegenerateActions).
   *
   * Uses the generic AgentPromptPrefixService to apply enhancement prompts for all agents
   * including regulations_agent and via_agent.
   */
  onResponseOptionsMenuAction(payload: { chatdata: any; action: 'regenerate' | 'crisp' | 'detailed' }): void {
    try {
      const { chatdata, action } = payload;
      const rawQuestion = chatdata?.question ?? '';
      const div = document.createElement('div');
      div.innerHTML = rawQuestion;
      const plainQuestion = (div.textContent || div.innerText || '').trim();
      if (!plainQuestion) return;

      // Get enhancement prompts for the current agent using the generic prompt prefix service
      const prompts = this.agentPromptPrefixService.getEnhancementPrompts(this.agentId);

      // Remove any existing prompt prefixes from the question
      const phrasesToRemove = Object.values(prompts)
        .map((p: string) => p.trim())
        .filter(Boolean)
        .map((p: string) => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|');
      
      const cleanQuestion = phrasesToRemove
        ? plainQuestion.replace(new RegExp(phrasesToRemove, 'gi'), '').trim()
        : plainQuestion;
      
      // Apply the selected enhancement prompt
      const enhancedPrompt = prompts[action]?.trim() || '';
      const enhancedQuestion = enhancedPrompt ? `${enhancedPrompt} ${cleanQuestion}`.trim() : cleanQuestion;

      this.onSendRequest(enhancedQuestion);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error in response options menu action', 'onResponseOptionsMenuAction', error);
    }
  }

  // ---------------------------------------------------------------------------
  // FEEDBACK (like / dislike / submit – regulations agent via bridge, same as chatbot v2)
  // ---------------------------------------------------------------------------

  /** FeedbackOptions from config.json – passed to chat interface for dislike form. */
  get feedbackOptionsFromConfig(): any[] {
    return (config as any)?.FeedbackOptions || [];
  }

  /**
   * Helper to check if both like and dislike are cleared (reset state).
   * Same logic as chatbot v2 VerifyIsReset.
   */
  private verifyIsReset(isLike: boolean, isDislike: boolean): boolean {
    return !isLike && !isDislike;
  }

  /**
   * Like click – save feedback via centralized save_agent_feedback.
   */
  onFeedbackLikeClick(event: { chatdata: any; isLike: boolean }): void {
    try {
      const { chatdata, isLike } = event;
      const chatHistoryId = chatdata.pk_chathistoryid;
      if (!chatHistoryId) return;

      const isReset = this.verifyIsReset(isLike, chatdata.disLikeClassAdded ?? false);

      // Sync feedback state back to agentChatHistory so it survives re-conversion
      this.syncFeedbackToHistory(chatHistoryId, isReset ? null : isLike);

      this.saveAgentFeedback(chatHistoryId, isReset ? null : isLike, isReset, '', chatdata);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error in onFeedbackLikeClick', 'onFeedbackLikeClick', error);
    }
  }

  /**
   * Dislike undo – reset feedback via centralized save_agent_feedback.
   */
  onFeedbackDislikeUndo(event: { chatdata: any }): void {
    try {
      const { chatdata } = event;
      const chatHistoryId = chatdata.pk_chathistoryid;
      if (!chatHistoryId) return;

      // Sync feedback state back to agentChatHistory so it survives re-conversion
      this.syncFeedbackToHistory(chatHistoryId, null);

      this.saveAgentFeedback(chatHistoryId, null, true, '', chatdata);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error in onFeedbackDislikeUndo', 'onFeedbackDislikeUndo', error);
    }
  }

  /**
   * Feedback form submitted – save via centralized save_agent_feedback.
   */
  onFeedbackSubmit(event: { chatdata: any; payload: any }): void {
    try {
      const { chatdata, payload } = event;
      const chatHistoryId = chatdata.pk_chathistoryid;
      if (!chatHistoryId) return;

      // Build feedback dict from selectedOptionIds (same structure as chatbot v2 FeedbackCBDict)
      const feedbackDict: { [key: string]: string } = {};
      if (payload.selectedOptionIds && chatdata.FeedbackOptions) {
        payload.selectedOptionIds.forEach((id: number | string) => {
          const option = chatdata.FeedbackOptions.find((opt: any) => opt.Id === id);
          if (option) {
            feedbackDict[String(id)] = option.Name;
          }
        });
      }

      const comments = payload.additionalComments?.length > 0 ? payload.additionalComments : ' ';
      const isLike = payload.isLike === true;

      // Sync feedback state back to agentChatHistory so it survives re-conversion
      this.syncFeedbackToHistory(chatHistoryId, isLike, feedbackDict, comments);

      this.saveAgentFeedbackWithComment(chatHistoryId, isLike, comments, chatdata);

      // Clear form state after submission
      chatdata.AdditionalComments = '';
      if (chatdata.FeedbackOptions) {
        chatdata.FeedbackOptions.forEach((opt: any) => (opt.IsChecked = false));
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error in onFeedbackSubmit', 'onFeedbackSubmit', error);
    }
  }

  /**
   * Revert feedback UI state on the chatdata item and agentChatHistory
   * when save_response_feedback API fails or response_id is invalid.
   * Clears like/dislike so the "Thanks" message disappears, and shows
   * the error toast (same as chatbot's error-toast-message pattern).
   */
  private revertFeedbackState(chatdata: any, responseId: string | number): void {
    try {
      chatdata.LikeClassAdded = false;
      chatdata.disLikeClassAdded = false;
      chatdata.SubmitFeedback = false;

      // Revert the agentChatHistory sync as well
      this.syncFeedbackToHistory(responseId, null);

      // Re-create ChatQueriesList from the updated history so the template
      // picks up the reverted state even if the chatdata reference above was stale
      // (convertAgentChatToChatQueriesList creates new objects each time)
      if (this.chatHistory && this.chatHistory.length > 0) {
        const newChatQueriesList = this.convertAgentChatToChatQueriesList(this.chatHistory);
        this.ChatQueriesList = [...newChatQueriesList];
      }

      // Show error toast (same as chatbot's IsCustomErrorMessageVisible pattern)
      this.feedbackErrorVisible = true;
      this.feedbackErrorText =  this.translate.instant('FEEDBACK_ERROR_TEXT');
      this.cdr.detectChanges();

      setTimeout(() => {
        this.feedbackErrorVisible = false;
        this.feedbackErrorText = '';
        this.cdr.detectChanges();
      }, 4000);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error reverting feedback state', 'revertFeedbackState', error);
    }
  }

  /** Close the feedback error toast manually. */
  closeFeedbackError(): void {
    this.feedbackErrorVisible = false;
    this.feedbackErrorText = '';
    this.cdr.detectChanges();
  }

  /** Show the green feedback success toast and auto-dismiss after 4 seconds.
   *  If already visible, resets the toast so the slide-down animation replays. */
  private showFeedbackSuccess(): void {
    if (this.feedbackSuccessTimer) {
      clearTimeout(this.feedbackSuccessTimer);
      this.feedbackSuccessTimer = null;
    }

    if (this.feedbackSuccessVisible) {
      this.feedbackSuccessVisible = false;
      this.cdr.detectChanges();
      setTimeout(() => {
        this.feedbackSuccessVisible = true;
        this.cdr.detectChanges();
        this.feedbackSuccessTimer = setTimeout(() => {
          this.feedbackSuccessVisible = false;
          this.cdr.detectChanges();
        }, 6000);
      });
    } else {
      this.feedbackSuccessVisible = true;
      this.cdr.detectChanges();
      this.feedbackSuccessTimer = setTimeout(() => {
        this.feedbackSuccessVisible = false;
        this.cdr.detectChanges();
      }, 6000);
    }
  }

  /** Close the feedback success toast manually. */
  closeFeedbackSuccess(): void {
    if (this.feedbackSuccessTimer) {
      clearTimeout(this.feedbackSuccessTimer);
      this.feedbackSuccessTimer = null;
    }
    this.feedbackSuccessVisible = false;
    this.cdr.detectChanges();
  }

  onTableDownloaded(fileName: string): void {
    this.tableDownloadSuccessToast.show(fileName);
  }

  closeTableDownloadSuccess(): void {
    this.tableDownloadSuccessToast.hide();
  }

  onTableDownloadFailed(): void {
    this.tableDownloadErrorToast.show();
  }

  onFilePreviewRequested(request: AgentFilePreviewRequest): void {
    this.documentPreviewRequest = request;
    this.isDocumentPreviewOpen = true;
    this.cdr.detectChanges();
  }

  onDocumentPreviewClosed(): void {
    this.isDocumentPreviewOpen = false;
    this.documentPreviewRequest = null;
    this.cdr.detectChanges();
  }

  onFileDownloadCompleted(fileName: string): void {
    this.fileDownloadSuccessToast.show(fileName);
  }

  onFileDownloadFailed(fileName: string): void {
    this.fileDownloadErrorToast.show(fileName);
  }

  onFilePreviewFailed(fileName: string): void {
    this.filePreviewErrorToast.show(fileName);
  }

  closeFileDownloadSuccess(): void {
    this.fileDownloadSuccessToast.hide();
  }

  closeFileDownloadError(): void {
    this.fileDownloadErrorToast.hide();
  }

  closeFilePreviewError(): void {
    this.filePreviewErrorToast.hide();
  }

  closeTableDownloadError(): void {
    this.tableDownloadErrorToast.hide();
  }

  /**
   * Check if a response_id is a real backend ID (UUID/string from the API)
   * vs a fallback index-based number (1, 2, 3) generated by the converter
   * when the backend doesn't send response_id in the SSE stream.
   */
  private isValidResponseId(responseId: string | number | undefined | null): boolean {
    if (responseId == null || responseId === '') return false;
    if (typeof responseId === 'number') return false;
    // A real response_id is a non-empty string (typically a UUID)
    return typeof responseId === 'string' && responseId.trim().length > 0;
  }

  /**
   * Generic feedback API call for non-regulations agents (e.g. Via agent).
   * Calls /save_response_feedback with the agent-specific endpoint config.
   *
   * If response_id is not a valid backend ID (e.g. fallback index 1,2,3),
   * the API call is skipped, the UI is reverted, and an error message is shown.
   *
   * @param chatdata - Optional chatdata item to revert UI state on error
   */
  private saveAgentFeedback(responseId: string | number, isLike: boolean | null, isReset: boolean, comment: string = '', chatdata?: any): void {
    try {
      if (!this.isValidResponseId(responseId)) {
        this.logger.logError(
          `[AgentChatContainer] Cannot save feedback – no valid response_id from backend (got: ${responseId}).`,
          'saveAgentFeedback'
        );
        if (chatdata) {
          this.revertFeedbackState(chatdata, responseId);
        }
        return;
      }

      const endpoint = this.agentApiService.getApiUrlFromConfig('save_response_feedback', this.agentId);
      if (!endpoint) {
        this.logger.logInfo(`[AgentChatContainer] No save_response_feedback endpoint for agent: ${this.agentId}`, 'saveAgentFeedback');
        if (chatdata) {
          this.revertFeedbackState(chatdata, responseId);
        }
        return;
      }

      const customBaseUrl = this.agentApiService.getBaseUrlForEndpoint('save_response_feedback', this.agentId);

      // Build payload based on whether agent uses centralized API
      const supervisor = this.agentApiService.getSupervisorForAgent(this.agentId);
      let payload: any;

      if (supervisor && supervisor.length > 0) {
        // Centralized API format: { tenant_id, user_id, message_id, feedback: { like, comments } }
        payload = {
          tenant_id: environment.TenantId || '',
          user_id: environment.LoginUserID || '',
          message_id: responseId,
          feedback: isReset
            ? { like: null, comments: null }
            : { like: isLike, comments: comment || '' }
        };
      } else {
        // Agent-owned format: { tenant_id, user_id, response_id, thread_id, is_like, comment }
        payload = {
          tenant_id: environment.TenantId || '',
          user_id: environment.LoginUserID || '',
          response_id: responseId,
          thread_id: this.getCurrentAgentThreadId() || '',
          is_like: isReset ? null : isLike,
          comment: isReset ? null : (comment || '')
        };
      }

      this.logger.logInfo(
        `[AgentChatContainer] saveAgentFeedback – agent: ${this.agentId}, response_id: ${responseId}, isReset: ${isReset}`,
        'saveAgentFeedback'
      );

      this.agentApiService.post<any>(endpoint, payload, undefined, customBaseUrl).subscribe({
        next: (res: any) => {
          this.logger.logInfo(`[AgentChatContainer] Feedback saved for ${this.agentId} – response_id: ${responseId}`, 'saveAgentFeedback');
          if (!isReset) this.showFeedbackSuccess();
        },
        error: (err: any) => {
          this.logger.logError(`[AgentChatContainer] Feedback save failed for ${this.agentId} – response_id: ${responseId}, error: ${err?.message || err}`, 'saveAgentFeedback');
          if (chatdata) {
            this.revertFeedbackState(chatdata, responseId);
          }
        }
      });
    } catch (error: any) {
      this.logger.logError(`[AgentChatContainer] saveAgentFeedback exception: ${error?.message || error}`, 'saveAgentFeedback');
    }
  }

  /**
   * Convenience wrapper for feedback submit from the dislike form (with comment).
   */
  private saveAgentFeedbackWithComment(responseId: string | number, isLike: boolean | null, comment: string, chatdata?: any): void {
    this.saveAgentFeedback(responseId, isLike, false, comment, chatdata);
  }

  /**
   * Sync feedback state back to agentChatHistory so that when
   * convertAgentChatToChatQueriesList re-runs (e.g. after a follow-up
   * question is asked), the previous message's feedback is preserved.
   *
   * Without this, "present" question feedback is only stored on the
   * ChatQueriesList item and lost on re-conversion. Old (API-loaded)
   * questions already have is_like/feedback in AgentChatMessage, so
   * they are unaffected.
   *
   * Matching strategy (works for ALL agents):
   * 1. Match by response_id (regulations agent and agents that emit response_id)
   * 2. Fallback: when response_id is absent on history messages (e.g. Via agent),
   *    pk_chathistoryid is set to (index + 1) by the converter, so use that
   *    numeric id to index into the history array.
   */
  private syncFeedbackToHistory(
    responseId: string | number,
    isLike: boolean | null,
    feedback?: any,
    comments?: string
  ): void {
    try {
      const currentHistory = this.agentOrchestrator.getAgentChatHistory();
      if (!currentHistory || currentHistory.length === 0) return;

      // 1. Try matching by response_id
      let message = currentHistory.find(
        (m: any) => m.response_id != null && (m.response_id === responseId || m.response_id === String(responseId))
      );

      // 2. Fallback: pk_chathistoryid = (index + 1) when response_id is absent.
      //    If responseId is a positive number within range, use it as the index.
      if (!message && typeof responseId === 'number' && responseId > 0 && responseId <= currentHistory.length) {
        message = currentHistory[responseId - 1];
      }

      if (message) {
        (message as any).is_like = isLike;
        if (isLike === null) {
          // Reset: clear feedback and comments so history and reload show normal state
          (message as any).feedback = null;
          (message as any).comment = null;
        } else {
          if (feedback !== undefined) {
            (message as any).feedback = feedback;
          }
          if (comments !== undefined) {
            (message as any).comment = comments;
          }
        }
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error syncing feedback to history', 'syncFeedbackToHistory', error);
    }
  }

  /**
   * Rebuild ChatQueriesList from current agentChatHistory so like/dislike/reset state is reflected in the UI.
   */
  private refreshChatListFromHistory(): void {
    const history = this.agentOrchestrator.getAgentChatHistory();
    if (history && history.length > 0) {
      this.ChatQueriesList = [...this.convertAgentChatToChatQueriesList(history)];
      this.cdr.detectChanges();
    }
  }

  /**
   * Forward prompt library dataset selection to chatbot ref when available (e.g. Regulations Agent).
   */
  onPromptLibraryNewSelectedDataset(dataset: any): void {
    try {
      if (this.chatbotComponentRef && typeof (this.chatbotComponentRef as any).onNewSelectedDataset === 'function') {
        (this.chatbotComponentRef as any).onNewSelectedDataset(dataset);
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error in prompt library dataset selection', 'onPromptLibraryNewSelectedDataset', error);
    }
  }

  /**
   * Handle domain suggestion selected from prompt-section dropdown.
   * Directly adds the selected domain code to userSelectedDomains
   * (the code is known from the event).
   */
  onPromptSectionSuggestionSelected(domain: string): void {
    try {
      // Directly add the selected domain code (deduplicated)
      if (domain) {
        const upperCode = domain.toUpperCase();
        if (!this.userSelectedDomains.includes(upperCode)) {
          this.userSelectedDomains = [...this.userSelectedDomains, upperCode];
        }
      }

      // Forward to chatbot ref when available (backward compat)
      if (this.chatbotComponentRef && typeof (this.chatbotComponentRef as any).onSuggestionSelected === 'function') {
        (this.chatbotComponentRef as any).onSuggestionSelected(domain);
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error in domain suggestion selection', 'onPromptSectionSuggestionSelected', error);
    }
  }

  /**
   * Handle domain inserted from prompt-section.
   * Forwards to chatbot ref when available.
   */
  onPromptSectionDomainInserted(): void {
    try {

      // Forward to chatbot ref when available (backward compat)
      if (this.chatbotComponentRef && typeof (this.chatbotComponentRef as any).onDomainInserted === 'function') {
        (this.chatbotComponentRef as any).onDomainInserted();
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error in domain inserted', 'onPromptSectionDomainInserted', error);
    }
  }

  /**
   * Handle pill/suggestion click from chat interface
   * 
   * @param pill - Pill text
   */
  onPillClick(pill: string): void {
    try {
      if (!this.streamFullyComplete) {
        return;
      }
      this.agentOrchestrator.addAgentMessage(pill, this.getSendTimeRetrievalMode());
      this.callAgentApi(pill);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error handling pill click', 'onPillClick', error);
    }
  }

  /**
   * Metadata "View all sources" / close history – request close left sidebar (same as chatbot).
   */
  onMetadataCloseHistory(): void {
    this.requestCloseSidebar.emit();
  }

  /**
   * Metadata "Display source panel" – open source panel on the right and close left sidebar (regulation agent only).
   */
  onMetadataDisplaySourcePanel(event: any): void {
    if (event && typeof event === 'object') {
      this.currentSourceData = {
        chatHistoryId: event.chatHistoryId != null ? String(event.chatHistoryId) : '',
        module_name: event.module_name != null ? String(event.module_name) : ''
      };
    } else {
      this.currentSourceData = null;
    }
    this.sourcePanelConfigData = { ...this.metadataConfigData };
    this.isSourcePanelVisible = true;
    this.requestCloseSidebar.emit();
    this.cdr.detectChanges();
  }

  /**
   * Close the source panel (view all sources).
   */
  closeSourcePanel(): void {
    this.isSourcePanelVisible = false;
    this.currentSourceData = null;
    this.sourcePanelConfigData = null;
    this.cdr.detectChanges();
  }

  /**
   * Call agent API with SSE streaming support
   * 
   * @param question - User question
   */
  private callAgentApi(question: string): void {
    try {
      // IMMEDIATELY clear SSE state to prevent old response from showing when new question is asked
      this.sseBuffer = '';
      this.sseFullMessageText = '';
      this.sseImageVisualization = '';
      this.ssePills = [];
      this.sseBubbles = [];
      this.sseErrorMessage = '';
      this.sseMetadata = [];
      this.sseResponseId = undefined;
      this.sseSaveTriggered = false;
      this.sseCurrentQuestion = question;
      this.promptPills = [];
      this.isBubblesLoading = false;
      if (this.agentId !== 'regulations_agent') {
        this.streamFullyComplete = false; // Re-enable bubbles only when final chunk (text="" is_complete=true) arrives
      }
      
      this.logger.logInfo('[AgentChatContainer] Cleared SSE state for new question', 'callAgentApi', `Question: "${question.substring(0, 50)}..."`);
      
      // Set streaming state IMMEDIATELY when user asks question. Main loader not shown during stream; bubbles loader shows from content-complete to final chunk.
      this.streamingStateService.setStreamingState(true);
      this.internalLoaderShow = false;
      
      // Show engaging loader with minimal action to ensure it displays
      this.showEngagingLoader = true;
      this.engagingLoaderIsStreaming = true;
      this.engagingLoaderIsCompleted = false;
      this.engagingLoaderCurrentAction = 'Processing';
      
      // Reset engaging loader and reasoning state for new question
      this.engagingLoaderSteps = [];
      this.stopReasoningAnimation();
      this.reasoningSteps = [];
      this.showReasoning = false;
      this.activeReasoningIndex = 0;
      
      // Initialize streaming UI immediately so response area shows loading state
      this.updateSSEUI(true, false);
      
      // Resolve userId and tenantId from environment (works in both v2 and v3 flows)
      const userId = (this.chatbotComponentRef?.UserID) || environment.LoginUserID || '';
      const tenantId = environment.TenantId || '';
      
      // Get thread_id (session ID). When in agent mode, use only agent thread state so
      // a new question from Via/agent landing always creates a new session when appropriate.
      // When isNewAgentChat and no orchestrator thread, always use a new thread (e.g. after hub → agent).
      let threadId = '';
      const orchestratorThreadId = this.agentOrchestrator.getCurrentThreadId();
      const treatAsNewSession = this.isNewAgentChat && !orchestratorThreadId;
      if (orchestratorThreadId && !treatAsNewSession) {
        threadId = orchestratorThreadId;
        this.currentAgentThreadId = orchestratorThreadId; // Update local cache
      } else if (this.currentAgentThreadId && !treatAsNewSession) {
        threadId = this.currentAgentThreadId;
      } else if (this.agentId === 'module' || !this.agentId) {
        // Only use chatbot session IDs when in module (non-agent) mode
        if (this.chatbotComponentRef?.sessionIdArray?.length && this.chatbotComponentRef.sessionIdArray.length > 0) {
          threadId = this.chatbotComponentRef!.sessionIdArray[0];
        } else if (this.chatbotComponentRef?.currentSessionId?.length && this.chatbotComponentRef.currentSessionId.length > 0) {
          threadId = this.chatbotComponentRef!.currentSessionId[0].session_id;
        }
      }
      if (!threadId) {
        threadId = this.generateThreadId();
        this.currentAgentThreadId = threadId;
      }

      // Prepare SSE request payload (domain handling mirrors chatbot v2 lines 2402-2403)
      const ssePayload: any = {
        question: question,
        tenant_id: tenantId,
        user_id: userId,
        thread_id: threadId
      };

      if (this.agentId === 'ria_agent') {
        ssePayload.domain = environment.DomainName || '';
      } else {
        ssePayload.selected_domains = this.promptSectionIsDomainManagementEnabled
          ? this.getSelectedDomains()
          : [environment.DomainName];
        ssePayload.available_domains = this.promptSectionIsDomainManagementEnabled
          ? this.getAvailableDomains()
          : [environment.DomainName];
      }

      // Call SSE streaming API
      this.callAgentStreamingApi(ssePayload);

    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error calling agent API', 'callAgentApi', error);
      const errorMessage = "An error occurred while calling the API. Please try again.";
      this.agentOrchestrator.updateLatestAgentMessage(errorMessage, []);
      // Reset streaming state on error
      this.streamingStateService.setStreamingState(false);
      this.internalLoaderShow = false;
      this.isResponseStreaming = false;
      this.isMetadataLoading = false;
    }
  }


  /**
   * Call Agent Streaming API using Server-Sent Events (SSE)
   * Similar to old chatbot SSE implementation - processes HttpEvent stream
   * 
   * Handles two event types:
   * 1. reasoning: Complete sentences (is_complete always true), multiple events possible
   * 2. message: Token-level streaming (is_complete false until final token, then true)
   * 
   * @param ssePayload - SSE request payload {question, tenant_id, user_id, thread_id}
   */
  private callAgentStreamingApi(ssePayload: any): void {
    try {
      // Set the latest thread ID for SSE streaming (SSE state already cleared in callAgentApi)
      this.sseLatestThreadId = ssePayload.thread_id;
      this.sseImageVisualization = '';
      this.ssePills = [];
      this.sseBubbles = [];
      this.sseErrorMessage = '';
      this.sseMetadata = [];
      this.isConversationSaved = false; // Reset until conversation_saved event fires
      this.isFollowUpQuestionsReceived = false; // Reset until follow_up_q event is received
      
      // Reset reasoning state
      this.stopReasoningAnimation();
      this.reasoningSteps = [];
      this.showReasoning = false;
      this.activeReasoningIndex = 0;
      
      // Reset engaging loader state for new question (keep it enabled for this new question)
      this.engagingLoaderSteps = [];
      
      let preValue = '';
      let zeroEventCount = 0;
      let isErrorFound = false;

      if (this.streamingSubscription) {
        this.streamingSubscription.unsubscribe();
      }

      /** Route all agents through centralized SSE streaming (VIA/RIA/regulations). */
      const streaming$ = this.streamingApiService.agentStreamingApi(ssePayload, this.agentId);

      // Subscribe to HttpEvent stream (similar to chatbot's callChunkedApi)
      this.streamingSubscription = streaming$.subscribe({
        next: (event: HttpEvent<any>) => {
          try {
            // Check event type
            if (event.type === 0) {
              zeroEventCount++;
            }

            // Error handling: If too many zero events, show error
            if (zeroEventCount >= 3 && !isErrorFound) {
              isErrorFound = true;
              const errorMessage = "We apologize for the inconvenience. It seems the service is currently unavailable. Please try again later, or feel free to contact our support team for assistance. Thank you for your patience.";
              this.agentOrchestrator.updateLatestAgentMessage(errorMessage, []);
              // Reset streaming state on error
              this.streamingStateService.setStreamingState(false);
              this.internalLoaderShow = false;
              return;
            }

            if (!isErrorFound) {
              // Handle upload progress
              if (event.type === HttpEventType.UploadProgress) {
                // Upload in progress
              }
              // Handle download progress (SSE data arrives here)
              else if (event.type === HttpEventType.DownloadProgress) {
                const data = event as any;
                const partialText = data.partialText;

                if (partialText) {
                  // Extract new data since last update
                  const newData = partialText.replace(preValue, '');
                  preValue = partialText;

                  this.logger.logInfo(`[AgentChatContainer] SSE Data Received - New chunk length: ${newData.length}, Total length: ${partialText.length}`, 'callAgentStreamingApi', '');
                  
                  if (newData.trim()) {
                    this.logger.logInfo(`[AgentChatContainer] Processing new SSE chunk:`, 'callAgentStreamingApi', newData);
                    // Parse SSE format
                    this.parseSSEData(newData);
                  }
                }
              }
              // Handle response complete
              else if (event.type === HttpEventType.Response) {
                // Stream completed
                this.logger.logInfo(`[AgentChatContainer] SSE response complete. Thread ID: ${this.sseLatestThreadId}`, 'callAgentStreamingApi', '');

                // Process any remaining SSE data that arrived after the last DownloadProgress event
                const responseBody = (event as any)?.body;
                if (typeof responseBody === 'string' && responseBody) {
                  const remainingData = responseBody.replace(preValue, '');
                  if (remainingData.trim()) {
                    this.logger.logInfo('[AgentChatContainer] Processing remaining SSE data from response body', 'callAgentStreamingApi', `length: ${remainingData.length}`);
                    this.parseSSEData(remainingData);
                  }
                  // Flush any partial line left in the buffer
                  if (this.sseBuffer.trim()) {
                    this.parseSSEData('\n');
                  }
                }

                // Mark conversation as saved (enables follow-up questions, hides saving loader)
                this.isConversationSaved = true;
                this.internalLoaderShow = false;

                // Capture response_id from the streaming response (e.g. conversation_saved
                // event from WebSocket bridge) so the share feature can use it.
                // Works for any agent whose streaming emits response_id in the final body.
                const responseId = (typeof responseBody === 'object' && responseBody != null) ? responseBody?.response_id : undefined;
                if (responseId) {
                  this.agentOrchestrator.setLatestMessageResponseId(responseId);
                  this.logger.logInfo(
                    `[AgentChatContainer] Captured response_id for share: ${responseId}`,
                    'callAgentStreamingApi'
                  );
                }

                // Finalize streaming state: ensure pills are visible even if the final empty message was lost
                this.finalizeStreamState();

                this.cdr.detectChanges();
              }
            }
          } catch (error: any) {
            this.logger.logInfo('[AgentChatContainer] Error in API response handler', 'callAgentStreamingApi', error);
            const errorMessage = "An error occurred while processing the response. Please try again.";
            this.agentOrchestrator.updateLatestAgentMessage(errorMessage, []);
            // Reset streaming state on error
            this.streamingStateService.setStreamingState(false);
            this.internalLoaderShow = false;
            this.isConversationSaved = true; // Allow follow-ups on error
            if (this.agentId !== 'regulations_agent') {
              this.streamFullyComplete = true; // Allow bubble clicks on error
            }
          }
        },
        error: (error: any) => {
          this.logger.logInfo('[AgentChatContainer] SSE API call error', 'callAgentStreamingApi', error);
          const errorMessage = "We apologize for the inconvenience. It seems the service is currently unavailable. Please try again later, or feel free to contact our support team for assistance. Thank you for your patience.";
          this.agentOrchestrator.updateLatestAgentMessage(errorMessage, []);
          this.streamingStateService.setStreamingState(false);
          this.internalLoaderShow = false;
          this.isResponseStreaming = false;
          this.isConversationSaved = true;
          this.showEngagingLoader = false;
          this.engagingLoaderIsStreaming = false;
          this.engagingLoaderIsCompleted = false;
          this.engagingLoaderSteps = [];
          this.engagingLoaderCurrentAction = '';
          if (this.agentId !== 'regulations_agent') {
            this.streamFullyComplete = true;
          }
        },
        complete: () => {
          this.logger.logInfo(`[AgentChatContainer] SSE stream completed. Thread ID: ${this.sseLatestThreadId}`, 'callAgentStreamingApi', '');
          // Safety net: finalize streaming state in case Response handler didn't fire or didn't fully resolve
          this.finalizeStreamState();
        }
      });
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error calling Agent Streaming API', 'callAgentStreamingApi', error);
      const errorMessage = "An error occurred while calling the API. Please try again.";
      this.agentOrchestrator.updateLatestAgentMessage(errorMessage, []);
      this.streamingStateService.setStreamingState(false);
      this.internalLoaderShow = false;
      this.isResponseStreaming = false;
      this.isConversationSaved = true;
      this.showEngagingLoader = false;
      this.engagingLoaderIsStreaming = false;
      this.engagingLoaderIsCompleted = false;
      this.engagingLoaderSteps = [];
      this.engagingLoaderCurrentAction = '';
      if (this.agentId !== 'regulations_agent') {
        this.streamFullyComplete = true;
      }
    }
  }

  /**
   * Finalize streaming state when the SSE stream ends (Response event or observable complete).
   * Ensures pills/bubbles are visible even if the final empty-text message was lost due to chunking.
   * Safe to call multiple times - it's a no-op when streaming already ended.
   */
  private finalizeStreamState(): void {
    if (!this.isResponseStreaming && this.streamFullyComplete) {
      return; // Already finalized
    }

    this.logger.logInfo('[AgentChatContainer] Finalizing stream state as safety net', 'finalizeStreamState', '');

    this.streamFullyComplete = true;
    this.isBubblesLoading = false;

    // Propagate any collected bubbles/pills to promptPills so they appear in the UI
    const hasPillsOrBubbles = (this.ssePills?.length ?? 0) > 0 || (this.sseBubbles?.length ?? 0) > 0;
    if (hasPillsOrBubbles) {
      this.promptPills = [...this.sseBubbles, ...this.ssePills];
    }

    this.updateSSEUI(false, true);
    this.cdr.detectChanges();
  }

  /** RIA-only: throttle visible UI updates while tokens arrive. */
  private shouldThrottleRiaStreamUi(): boolean {
    return this.agentId === 'ria_agent';
  }

  private cancelRiaStreamUiFlush(): void {
    if (this.riaStreamFlushRafId !== null) {
      cancelAnimationFrame(this.riaStreamFlushRafId);
      this.riaStreamFlushRafId = null;
    }
    if (this.riaStreamFlushTimeoutId !== null) {
      clearTimeout(this.riaStreamFlushTimeoutId);
      this.riaStreamFlushTimeoutId = null;
    }
  }

  private scheduleRiaStreamUiFlush(): void {
    if (this.riaStreamFlushRafId !== null || this.riaStreamFlushTimeoutId !== null) {
      return;
    }
    this.riaStreamFlushRafId = requestAnimationFrame(() => {
      this.riaStreamFlushRafId = null;
      const elapsed = Date.now() - this.riaStreamLastFlushAt;
      if (elapsed < AgentChatContainerComponent.RIA_STREAM_UI_MIN_INTERVAL_MS) {
        this.riaStreamFlushTimeoutId = setTimeout(() => {
          this.riaStreamFlushTimeoutId = null;
          this.flushRiaStreamUiToUi();
        }, AgentChatContainerComponent.RIA_STREAM_UI_MIN_INTERVAL_MS - elapsed);
        return;
      }
      this.flushRiaStreamUiToUi();
    });
  }

  private flushRiaStreamUiToUi(): void {
    this.riaStreamLastFlushAt = Date.now();
    this.updateSSEUI(true, false);
  }

  /** Patch only the in-flight RIA answer instead of remapping full chat history each token. */
  private canPatchLastChatQueryDuringRiaStream(history: AgentChatMessage[]): boolean {
    return (
      this.agentId === 'ria_agent' &&
      this.isResponseStreaming &&
      history.length > 0 &&
      this.ChatQueriesList.length === history.length &&
      !this.agentOrchestrator.getSessionLoadInProgress()
    );
  }

  private patchLastChatQueryFromStreamingHistory(latest: AgentChatMessage): void {
    const lastQuery = this.ChatQueriesList[this.ChatQueriesList.length - 1];
    if (!lastQuery || !latest) {
      return;
    }
    lastQuery.answer = latest.answer;
    lastQuery.isLoading = latest.isLoading ?? false;
    if (latest.image_visualization !== undefined) {
      lastQuery.image_visualization = latest.image_visualization;
    }
    if (latest.metadata !== undefined) {
      lastQuery.metadata = Array.isArray(latest.metadata)
        ? [...latest.metadata]
        : { ...latest.metadata };
    }
    if (latest.errorMessage !== undefined) {
      lastQuery.errorMessage = latest.errorMessage;
    }
    if (latest.pills !== undefined) {
      lastQuery.pills = latest.pills;
      lastQuery.followUpQuestions = latest.pills;
    }
  }

  /**
   * Helper method to update UI with current SSE state
   * Reduces code duplication across event handlers
   * 
   * @param isStreaming - Whether the response is currently streaming
   * @param showPills - Whether to display pills (only when complete)
   */
  private updateSSEUI(isStreaming: boolean = false, showPills: boolean = false): void {
    if (!isStreaming) {
      this.cancelRiaStreamUiFlush();
    }
    // Update streaming state service
    this.streamingStateService.setStreamingState(isStreaming);
    
    // Update loader state. Don't show main loader during message streaming; show bubbles loader (below response) from content-complete to final chunk instead.
    this.internalLoaderShow = false;

    // Track active response streaming state
    this.isResponseStreaming = isStreaming;
    
    // Set timestamp when streaming completes
    const updatedAt = !isStreaming
      ? (this.sseCompletionUpdatedAt || new Date().toISOString())
      : undefined;
    if (!isStreaming) {
      this.sseCompletionUpdatedAt = undefined;
    }
    
    // Pass completed reasoning steps when streaming is complete
    const reasoningStepsToSave = !isStreaming && this.engagingLoaderSteps.length > 0 ? this.engagingLoaderSteps : undefined;
    
    // Debug logging for streaming content
    this.logger.logInfo(
      `[AgentChatContainer] UpdateSSEUI - isStreaming: ${isStreaming}, isResponseStreaming: ${this.isResponseStreaming}, messageLength: ${this.sseFullMessageText.length}, content: "${this.sseFullMessageText.substring(0, 100)}..."`, 
      'updateSSEUI', 
      ''
    );
    
    const metadataToPass = this.sseMetadata.length > 0 ? this.sseMetadata : undefined;
    if (metadataToPass) {
      this.logger.logInfo('[AgentChatContainer] UpdateSSEUI passing metadata', 'updateSSEUI', `count: ${metadataToPass.length}`);
    }
    // Bubbles → prompt pills. Follow-up events → ssePills (Related Questions for regulations only).
    const pillsForPrompt = (this.sseBubbles.length > 0 || this.ssePills.length > 0)
      ? [...this.sseBubbles, ...this.ssePills]
      : this.ssePills;
    this.promptPills = pillsForPrompt;
    // Regulations Related Questions must only use follow_up pills, never bubble_item values.
    const pillsForMessage = this.agentId === 'regulations_agent'
      ? (this.ssePills.length > 0 ? [...this.ssePills] : [])
      : pillsForPrompt;
    this.agentOrchestrator.updateLatestAgentMessage(
      this.sseFullMessageText,
      pillsForMessage,
      this.sseImageVisualization.trim() || undefined,
      metadataToPass,
      this.sseErrorMessage || undefined,
      isStreaming,
      updatedAt,
      reasoningStepsToSave,
      this.sseResponseId
    );
    this.cdr.detectChanges();
    // Only scroll-to-bottom while tokens/SSE are actively streaming. Post-stream updates
    // (metadata, pills, history sync) must not call scrollToBottom or users cannot read
    // earlier messages (Via long markdown + layout growth made this especially visible).
    this.triggerAutoScroll(isStreaming);
  }

  /**
   * Parse SSE data format
   * Format: data: {"event": "reasoning|message|image_visualization|follow_up|error_message|metadata", "data": {...}}
   * 
   * @param newData - New SSE data chunk
   */
  private parseSSEData(newData: string): void {
    try {
      const dataToProcess = this.sseBuffer + newData;
      this.sseBuffer = '';

      const lines = dataToProcess.split('\n');

      // If data doesn't end with a newline, the last element may be a partial line — buffer it for the next chunk
      if (dataToProcess.length > 0 && !dataToProcess.endsWith('\n')) {
        this.sseBuffer = lines.pop() || '';
      }

      for (const line of lines) {
        const trimmedLine = line.trim();

        // Skip empty lines and ping messages
        if (!trimmedLine || trimmedLine.startsWith(': ping')) continue;

        // Extract data line: "data: {...}"
        if (!trimmedLine.startsWith('data:')) continue;

        const jsonData = trimmedLine.substring(5).trim();
        if (!jsonData) continue;

        try {
          const parsedData = JSON.parse(jsonData);
          
          // Check if this is a conversation_saved event (flat structure)
          if (parsedData.conversation_saved === true) {
            this.logger.logInfo('[AgentChatContainer] Conversation saved event received (flat format)', 'parseSSEData', JSON.stringify(parsedData));
            
            // Store thread_id and response_id from flat format event
            if (parsedData.thread_id) {
              this.sseLatestThreadId = parsedData.thread_id;
              this.currentAgentThreadId = parsedData.thread_id;
            }
            if (parsedData.response_id != null && parsedData.response_id !== '') {
              this.sseResponseId = String(parsedData.response_id);
              this.agentOrchestrator.setLatestMessageResponseId(this.sseResponseId);
            }
            
            this.handleConversationSavedEvent(parsedData);
            continue;
          }
          
          // Standard event format: {event: "type", data: {...}}
          const { event: eventType, data: eventData } = parsedData;
          
          if (!eventType) continue;

          // Store thread_id if provided
          if (eventData.thread_id) {
            this.sseLatestThreadId = eventData.thread_id;
            this.currentAgentThreadId = eventData.thread_id;
          }
          if (eventData.response_id != null && eventData.response_id !== '') {
            this.sseResponseId = String(eventData.response_id);
            // Set on latest message immediately so it persists even if event order is (message complete, then response_id)
            this.agentOrchestrator.setLatestMessageResponseId(this.sseResponseId);
          }

          // Session-aware streaming: Check if this response is for the current thread/session
          const currentThreadId = this.getCurrentAgentThreadId();
          if (this.sseLatestThreadId && currentThreadId && this.sseLatestThreadId !== currentThreadId) {
            this.logger.logInfo(
              `[AgentChatContainer] Ignoring response for different session. Expected: ${currentThreadId}, Got: ${this.sseLatestThreadId}`,
              'handleSSEResponse'
            );
            continue; // Skip processing this response chunk
          }

          // Unified format: event "data" with data_type (reference_item, visualization_item, etc.) – support for any backend/agent
          let dispatchEventType = eventType;
          if ((eventType === 'data') && eventData?.data_type) {
            dispatchEventType = eventData.data_type;
          }

          switch (dispatchEventType) {
            case 'reasoning':
              if (this.agentId !== 'ria_agent') {
                this.logger.logInfo(`[AgentChatContainer] Processing reasoning event`, 'parseSSEData', '');
                this.handleReasoningEvent(eventData);
              }
              break;

            case 'message':
              this.logger.logInfo(`[AgentChatContainer] Processing message event`, 'parseSSEData', JSON.stringify(eventData));
              this.handleMessageEvent(eventData);
              break;

            case 'visualization_start':
              this.sseImageVisualization = '';
              break;

            case 'image_visualization':
            case 'visualization_item':
              if (eventData?.data?.code != null) {
                const code = eventData.data.code;
                this.sseImageVisualization = typeof code === 'string' && code.startsWith('data:') ? code : `data:image/png;base64,${code}`;
                this.updateSSEUI(this.isResponseStreaming, false);
              } else {
                this.handleImageVisualizationEvent(eventData);
              }
              break;

            case 'visualization_end':
              break;

            case 'followup_start':
              this.ssePills = [];
              break;

            case 'follow_up':
            case 'follow_up_q':
            case 'pills':
            case 'followup_item':
              if (
                eventData?.data?.question != null ||
                eventData?.data?.text != null ||
                eventData?.data?.value != null
              ) {
                const followUpQuestion = String(
                  eventData.data.question ?? eventData.data.text ?? eventData.data.value
                );
                this.pushStreamFollowUpQuestion(followUpQuestion);
              } else {
                this.handlePillsEvent(eventData);
              }
              break;

            case 'followup_end':
              this.updateSSEUI(this.isResponseStreaming, true);
              break;

            case 'error_message':
            case 'error':
              this.handleErrorEvent(eventData);
              break;

            case 'metadata':
              if (eventData?.data_type === 'metadata') {
                break;
              }
              this.beginMetadataLoading();
              this.handleMetadataEvent(eventData);
              break;

            case 'metadata_start':
            case 'reference_start':
            case 'citation_start':
              this.beginMetadataLoading();
              this.handleMetadataEvent(eventData);
              break;

            case 'reference_item':
            case 'citation_item':
              this.beginMetadataLoading();
              if (eventData?.data != null && typeof eventData.data === 'object') {
                if (!Array.isArray(this.sseMetadata)) this.sseMetadata = [];
                const metadataItem = dispatchEventType === 'citation_item' && isRegulationsAgent(this.agentId)
                  ? normalizeRegulationsCitation(eventData.data)
                  : eventData.data;
                if (metadataItem) {
                  this.sseMetadata.push(metadataItem);
                }
                this.updateSSEUI(this.isResponseStreaming, false);
              } else {
                this.handleMetadataEvent(eventData);
              }
              break;

            case 'metadata_complete':
            case 'reference_end':
            case 'citation_end':
              this.isMetadataLoading = false;
              this.updateSSEUI(this.isResponseStreaming, false);
              this.logger.logInfo('[AgentChatContainer] Metadata complete', 'parseSSEData', `items: ${this.sseMetadata?.length ?? 0}`);
              break;

            case 'superagent_artifact':
              if (isRegulationsAgent(this.agentId)) {
                this.handleRegulationsSuperagentArtifact(eventData?.data);
              }
              break;
            
            case 'conversation_saved':
              this.logger.logInfo('[AgentChatContainer] Conversation saved event received', 'parseSSEData', JSON.stringify(eventData));
              this.handleConversationSavedEvent(eventData);
              break;

            case 'bubble_start':
              this.sseBubbles = [];
              this.cdr.detectChanges();
              break;

            case 'bubble_item':
              if (eventData?.data?.value != null) {
                this.sseBubbles = this.sseBubbles || [];
                this.sseBubbles.push(String(eventData.data.value));
                this.updateSSEUI(this.isResponseStreaming, true);
              } else if (eventData?.data?.question != null) {
                this.ssePills = this.ssePills || [];
                this.ssePills.push(String(eventData.data.question));
                this.updateSSEUI(this.isResponseStreaming, true);
              } else {
                this.handlePillsEvent(eventData);
              }
              break;

            case 'bubble_end':
              this.updateSSEUI(this.isResponseStreaming, true);
              this.cdr.detectChanges();
              break;
          }

        } catch (parseError) {
          this.logger.logInfo('[AgentChatContainer] JSON parse error, skipping line', 'parseSSEData', '');
        }
      }
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error parsing SSE data', 'parseSSEData', error);
    }
  }

  /**
   * Handle reasoning event
   * Updates engaging loader display with real-time actions
   */
  private handleReasoningEvent(eventData: any): void {
    let reasoningStep: EngagingLoaderStep;
    
    // Handle new structured format with action and reasoning/rationale
    if (eventData.action) {
      const reasoningText = eventData.reasoning || eventData.rationale || '';
      reasoningStep = {
        action: eventData.action,
        reasoning: reasoningText,
        is_complete: eventData.is_complete || true,
        thread_id: eventData.thread_id,
        timestamp: new Date().toISOString()
      };
      
      // Update current engaging loader display
      this.engagingLoaderCurrentAction = eventData.action;
      
      // Enable engaging loader if not already enabled
      if (!this.showEngagingLoader) {
        this.showEngagingLoader = true;
        this.engagingLoaderIsStreaming = true;
        this.engagingLoaderIsCompleted = false;
        this.logger.logInfo('[AgentChatContainer] Enabling engaging loader for reasoning', 'handleReasoningEvent', '');
      }
      
      // Set generic step type for all reasoning events
      reasoningStep.type = 'analyzing';
      
      this.logger.logInfo(`[AgentChatContainer] Engaging Loader - Action: ${eventData.action}, Reasoning: ${eventData.reasoning || eventData.rationale || '(empty)'}`, 'handleReasoningEvent', '');
    }
    // Handle legacy text format
    else if (eventData.text || eventData.reasoning) {
      const reasoningText = eventData.text || eventData.reasoning;
      reasoningStep = {
        reasoning: reasoningText,
        is_complete: eventData.is_complete || true,
        thread_id: eventData.thread_id,
        timestamp: new Date().toISOString(),
        type: 'analyzing'
      };
      
        // Simple action for legacy format
        this.engagingLoaderCurrentAction = 'Analyzing';
      
      this.logger.logInfo('[AgentChatContainer] Engaging Loader: ' + reasoningText, 'handleReasoningEvent', '');
    }
    else {
      return; // Skip if no valid data
    }
    
      // Add to engaging loader steps for dropdown later
      this.engagingLoaderSteps.push(reasoningStep);
    
    this.cdr.detectChanges();
  }

  /**
   * Start reasoning animation - each step blinks then hides from top
   */
  private startReasoningAnimation(): void {
    // Clear any existing animation
    if (this.reasoningAnimationInterval) {
      clearInterval(this.reasoningAnimationInterval);
    }
    
    // Animate through steps every 1.5 seconds
    this.reasoningAnimationInterval = setInterval(() => {
      if (this.activeReasoningIndex < this.reasoningSteps.length - 1) {
        this.activeReasoningIndex++;
        this.cdr.detectChanges();
      } else {
        // Keep the last step blinking until message starts
        // Don't clear the interval - it will be cleared when message starts
      }
    }, 1500);
  }

  /**
   * Stop reasoning animation and clear state
   */
  private stopReasoningAnimation(): void {
    if (this.reasoningAnimationInterval) {
      clearInterval(this.reasoningAnimationInterval);
      this.reasoningAnimationInterval = null;
    }
  }

  /** Regulations agent only: joins streamed thinking SSE chunks with a space. */
  private appendRegulationsThinkingChunkText(existing: string, chunk: string): string {
    if (!chunk) {
      return existing;
    }
    if (!existing) {
      return chunk;
    }
    return `${existing} ${chunk}`;
  }

  /**
   * Accumulate regulations SSE thinking chunks into one engaging-loader step.
   * Each SSE thinking message is appended with a leading space; thinking is not shown in the response body.
   */
  private appendRegulationsThinkingStep(token: string): void {
    if (!isRegulationsAgent(this.agentId) || !token) {
      return;
    }

    if (!this.showEngagingLoader) {
      this.showEngagingLoader = true;
      this.engagingLoaderIsStreaming = true;
      this.engagingLoaderIsCompleted = false;
    }

    const lastStep = this.engagingLoaderSteps.length > 0
      ? this.engagingLoaderSteps[this.engagingLoaderSteps.length - 1]
      : undefined;

    if (lastStep && lastStep.action === THINKING_STEP_ACTION) {
      lastStep.reasoning = this.appendRegulationsThinkingChunkText(lastStep.reasoning || '', token);
      this.engagingLoaderSteps = [...this.engagingLoaderSteps];
    } else {
      this.engagingLoaderSteps = [
        ...this.engagingLoaderSteps,
        {
          action: THINKING_STEP_ACTION,
          reasoning: token,
          is_complete: false,
          type: 'analyzing'
        }
      ];
    }

    this.engagingLoaderCurrentAction = THINKING_STEP_ACTION;
    this.cdr.detectChanges();
  }

  /**
   * Drops the live engaging-loader state so a loaded thread renders its own persisted
   * thinking (`completedReasoningSteps`) instead of the last stream's steps.
   */
  private clearLiveEngagingLoader(): void {
    this.showEngagingLoader = false;
    this.engagingLoaderIsStreaming = false;
    this.engagingLoaderIsCompleted = false;
    this.engagingLoaderSteps = [];
    this.engagingLoaderCurrentAction = '';
  }

  /** Regulations: streamed thinking text, persisted via save_agent_response. */
  private getStreamedThinkingText(): string {
    return this.engagingLoaderSteps
      .filter((step) => step.action === THINKING_STEP_ACTION)
      .map((step) => step.reasoning || '')
      .join('')
      .trim();
  }

  /**
   * Handle message event (main response text)
   */
  private handleMessageEvent(eventData: any): void {
    this.logger.logInfo(`[AgentChatContainer] Message event received:`, 'handleMessageEvent', JSON.stringify(eventData));

    if (eventData.stream_type === 'thinking') {
      if (isRegulationsAgent(this.agentId)) {
        this.appendRegulationsThinkingStep(typeof eventData.text === 'string' ? eventData.text : '');
      } else {
        this.logger.logInfo(
          '[AgentChatContainer] Ignoring thinking stream message until text stream starts',
          'handleMessageEvent',
          ''
        );
      }
      return;
    }
    
    if (!eventData.text && !eventData.is_complete) {
      this.logger.logInfo(`[AgentChatContainer] No text in message event, skipping`, 'handleMessageEvent', '');
      return;
    }

      // Switch from engaging loader streaming to response streaming when actual message starts
      if (this.engagingLoaderIsStreaming && this.showEngagingLoader) {
        // Keep engaging loader visible but switch to completed state
        this.engagingLoaderIsStreaming = false;
        this.engagingLoaderIsCompleted = true;
        this.logger.logInfo('[AgentChatContainer] Switching to response streaming', 'handleMessageEvent', '');
      }

    // Hide old reasoning when actual message starts streaming (backward compatibility)
    if (this.showReasoning) {
      this.stopReasoningAnimation();
      this.showReasoning = false;
      this.reasoningSteps = [];
      this.activeReasoningIndex = 0;
    }

    // For final message: regulations/ria = is_complete enough; Via and others = is_complete and empty text (final chunk)
    const isFinalComplete =
      eventData.is_complete === true &&
      (this.agentId === 'regulations_agent' ||
        this.agentId === 'ria_agent' ||
        eventData.text === '' ||
        eventData.text == null);

    if (isFinalComplete) {
      this.applyStreamSourceMetadata(eventData);
      this.isMetadataLoading = false;
      if (
        (this.agentId === 'ria_agent' || this.agentId === 'regulations_agent') &&
        eventData.text &&
        !this.sseFullMessageText
      ) {
        this.sseFullMessageText = eventData.text;
      }

      this.logger.logInfo('[AgentChatContainer] Final complete message received', 'handleMessageEvent', '');
      this.streamFullyComplete = true; // Allow bubble clicks only after final chunk
      this.isBubblesLoading = false; // Stop bubbles loader on final chunk

      if (this.shouldPersistStreamedAgentResponse()) {
        this.sseCompletionUpdatedAt =
          typeof eventData.updated_at === 'string' && eventData.updated_at.trim().length > 0
            ? eventData.updated_at.trim()
            : undefined;
        this.triggerSaveAgentResponseFromStream(eventData);
      }
      
      // Complete engaging loader and show as dropdown
      this.engagingLoaderIsStreaming = false;
      this.engagingLoaderIsCompleted = true;
      this.engagingLoaderCurrentAction = '';
      
      // Only clear pills when none from backend; Via/regulations use backend bubbles when present
      const hasPillsOrBubbles = (this.ssePills?.length ?? 0) > 0 || (this.sseBubbles?.length ?? 0) > 0;
      if (!hasPillsOrBubbles) {
        this.ssePills = [];
        this.sseBubbles = [];
        this.promptPills = [];
      }
      
      this.cancelRiaStreamUiFlush();
      // Regulations keeps the stream/dots/spinner active until metadata_complete or conversation_saved,
      // matching development where sources can arrive one-by-one after the answer text.
      this.updateSSEUI(this.agentId === 'regulations_agent', true);
      
      this.logger.logInfo(`[AgentChatContainer] Token streaming complete. Thread ID: ${this.sseLatestThreadId}`, 'handleMessageEvent', '');
      
      
      // Message complete - session already exists in UI, no action needed
    } else if (eventData.is_complete === true && eventData.text) {
      // "content complete" chunk has full text – do not append (would duplicate). Bubbles follow, then empty-text final chunk. Start bubbles loader.
      this.isBubblesLoading = true;
      this.cdr.detectChanges();
      this.logger.logInfo('[AgentChatContainer] Message content complete, waiting for bubbles and final chunk', 'handleMessageEvent', '');
    } else {
      // Incremental streaming: accumulate text tokens
      const previousLength = this.sseFullMessageText.length;
      this.sseFullMessageText += eventData.text;

      if (this.shouldThrottleRiaStreamUi()) {
        this.scheduleRiaStreamUiFlush();
      } else {
        this.updateSSEUI(true, false);
      }

      this.logger.logInfo('[AgentChatContainer] Streaming token received', 'handleMessageEvent', `Token: "${eventData.text}", Previous: ${previousLength}, New: ${this.sseFullMessageText.length}`);
    }
  }


  /**
   * Handle image visualization event
   */
  private handleImageVisualizationEvent(eventData: any): void {
    const imageData = eventData.value || eventData.text;
    if (!imageData) return;

    this.sseImageVisualization += ` ${imageData} `;
    this.logger.logInfo('[AgentChatContainer] Image visualization received', 'handleImageVisualizationEvent', '');
    // Preserve streaming state so View all sources stays hidden until stream ends
    this.updateSSEUI(this.isResponseStreaming, false);
  }

  /**
   * Handle pills/follow-up questions event.
   * Preserve streaming state so the loader stays visible until the stream actually completes
   * (metadata and final message may still follow after follow_up_q).
   */
  private handlePillsEvent(eventData: any): void {
    const pillsData = eventData.value || eventData.pills || eventData.followUpQuestions;
    
    // For regulations agent: track when follow_up_q event is received to show dots loader
    // Show loader even if pills array is empty - the actual questions come later in conversation_saved event
    if (this.agentId === 'regulations_agent') {
      this.isFollowUpQuestionsReceived = true;
      this.cdr.detectChanges(); // Force UI update to show dots loader
    }
    
    if (pillsData && Array.isArray(pillsData) && pillsData.length > 0) {
      this.ssePills = pillsData;
      // Update pills above prompt section for next user interaction
      this.promptPills = pillsData;
      this.logger.logInfo('[AgentChatContainer] Follow-up questions/pills received from streaming', 'handlePillsEvent', '');
      this.updateSSEUI(this.isResponseStreaming, true);
    } else {
      // No pills from streaming: regulations = clear; Via and others = backend bubbles only (no mock), clear pills
      if (this.agentId === 'regulations_agent') {
         this.ssePills = [];
         this.promptPills = [];
         this.logger.logInfo('[AgentChatContainer] No followup questions received, clearing pills', 'handlePillsEvent', '');
      } else {
         this.ssePills = [];
         this.sseBubbles = [];
         this.promptPills = [];
      }
      this.updateSSEUI(this.isResponseStreaming, true);
    }
  }

  /**
   * Returns a random integer in [0, max) using crypto.getRandomValues when available (safe for any use).
   * Falls back to Math.random() only in environments without crypto (e.g. some tests).
   */
  private getRandomIndex(max: number): number {
    if (max <= 0) return 0;
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const buf = new Uint32Array(1);
      crypto.getRandomValues(buf);
      return (buf[0] % max) >>> 0;
    }
    return Math.floor(Math.random() * max); // NOSONAR S2245: Safe - fallback when crypto unavailable; used only for UI pill group selection, not security
  }

  /**
   * Generate static pills for testing
   * 3 pills are randomly selected from one of the predefined groups
   */
  private generateStaticPills(): void {
    try {
      const length = this.staticPillGroups.length;
      if (length === 0) {
        this.ssePills = [];
        this.promptPills = [];
        return;
      }
      const randomGroupIndex = this.getRandomIndex(length);
      const selectedGroup = this.staticPillGroups[randomGroupIndex];
      const pills: string[] = [...selectedGroup];
      
      // Update both ssePills and promptPills
      this.ssePills = pills;
      this.promptPills = pills;
      
      this.logger.logInfo(`[AgentChatContainer] Generated static pills with group ${randomGroupIndex + 1}`, 'generateStaticPills', '');
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error generating static pills', 'generateStaticPills', error);
    }
  }

  /**
   * Handle error message event
   */
  private handleErrorEvent(eventData: any): void {
    const errorMsg = eventData.value || eventData.message || eventData.text || eventData.error_message;
    
    if (errorMsg) {
      this.sseErrorMessage = errorMsg;
      // Show error in message if no text exists
      if (!this.sseFullMessageText) {
        this.sseFullMessageText = errorMsg;
      }
      this.logger.logInfo('[AgentChatContainer] Error message received: ' + errorMsg, 'handleErrorEvent', '');
      this.updateSSEUI(false, false); // Stop streaming on error
    }
  }

  /**
   * Handle metadata event (sources, references, etc.)
   */
  private beginMetadataLoading(): void {
    this.isMetadataLoading = true;
    if (this.agentId === 'regulations_agent') {
      this.isResponseStreaming = true;
      this.streamingStateService.setStreamingState(true);
    }
  }

  private applyStreamSourceMetadata(eventData: any): void {
    if (eventData.source_metadata) {
      this.handleMetadataEvent(eventData);
    }
    if (isRegulationsAgent(this.agentId)) {
      this.mergeRegulationsStreamMetadata(eventData.metadata);
    }
  }

  private mergeRegulationsStreamMetadata(metadata: unknown): void {
    const parsed = parseRegulationsStreamMetadata(metadata);
    if (parsed.citations && parsed.citations.length > 0) {
      this.sseMetadata = parsed.citations;
    }
    if (parsed.followUpQuestions && parsed.followUpQuestions.length > 0) {
      this.ssePills = parsed.followUpQuestions;
      this.isFollowUpQuestionsReceived = true;
    }
  }

  private handleRegulationsSuperagentArtifact(artifact: unknown): void {
    if (!artifact || typeof artifact !== 'object' || Array.isArray(artifact)) {
      return;
    }

    const payload = artifact as Record<string, unknown>;
    const responseText = typeof payload['response'] === 'string' ? payload['response'].trim() : '';
    if (responseText && !this.sseFullMessageText.trim()) {
      this.sseFullMessageText = responseText;
      this.updateSSEUI(true, false);
    }

    const thinkingText = typeof payload['thinking'] === 'string' ? payload['thinking'].trim() : '';
    if (thinkingText && !this.getStreamedThinkingText()) {
      this.setRegulationsThinkingFallback(thinkingText);
    }

    this.mergeRegulationsStreamMetadata(payload['metadata']);
    this.updateSSEUI(this.isResponseStreaming, false);
  }

  private setRegulationsThinkingFallback(thinking: string): void {
    if (!isRegulationsAgent(this.agentId) || !thinking) {
      return;
    }

    this.showEngagingLoader = true;
    this.engagingLoaderIsStreaming = false;
    this.engagingLoaderIsCompleted = true;
    this.engagingLoaderSteps = [
      {
        action: THINKING_STEP_ACTION,
        reasoning: thinking,
        is_complete: true,
        type: 'analyzing'
      }
    ];
    this.engagingLoaderCurrentAction = THINKING_STEP_ACTION;
    this.cdr.detectChanges();
  }

  private pushStreamFollowUpQuestion(question: string): void {
    const trimmed = question.trim();
    if (!trimmed) {
      return;
    }

    this.ssePills = this.ssePills || [];
    this.ssePills.push(trimmed);
    if (isRegulationsAgent(this.agentId)) {
      this.isFollowUpQuestionsReceived = true;
    }
    this.updateSSEUI(this.isResponseStreaming, true);
  }

  private handleMetadataEvent(eventData: any): void {
    const sourceMetadata = eventData.source_metadata;
    if (Array.isArray(sourceMetadata?.follow_up_questions)) {
      this.ssePills = sourceMetadata.follow_up_questions
        .filter((question: unknown): question is string => typeof question === 'string')
        .map((question: string) => question.trim())
        .filter((question: string) => question.length > 0);
    }
    const metadataValue = sourceMetadata?.references || eventData.value || eventData.metadata;
    
    if (!metadataValue || typeof metadataValue !== 'object') return;

    // Initialize metadata array if needed
    if (!Array.isArray(this.sseMetadata)) {
      this.sseMetadata = [];
    }

    // Handle object with keys (like chatbot's {"1": {...}, "2": {...}})
    if (!Array.isArray(metadataValue)) {
      Object.keys(metadataValue).forEach(key => {
        const metadataItem = metadataValue[key];
        if (metadataItem && typeof metadataItem === 'object') {
          this.sseMetadata.push({ key, ...metadataItem });
        }
      });
    } else {
      // Handle array directly
      this.sseMetadata = metadataValue;
    }

    this.logger.logInfo('[AgentChatContainer] Metadata received', 'handleMetadataEvent', '');
    // Preserve streaming state: only update message with metadata; don’t set isResponseStreaming = false
    // so "View all sources" stays hidden until streaming is actually complete (same as chatbot).
    this.updateSSEUI(this.isResponseStreaming, false);
  }


  /**
   * Whether the current agent must manually persist the streamed answer via save_agent_response.
   * RIA, VIA, and regulations persist through the centralized agent-common API.
   */
  private shouldPersistStreamedAgentResponse(): boolean {
    return this.agentId === 'ria_agent' || this.agentId === 'via_agent' || this.agentId === 'regulations_agent';
  }

  /**
   * Persist stream completion to the centralized save_agent_response API (RIA, VIA, Regulations).
   * Extracts supervisor, agents, response_time, etc. from the final message event.
   */
  private triggerSaveAgentResponseFromStream(eventData: Record<string, unknown>): void {
    if (this.sseSaveTriggered || !this.shouldPersistStreamedAgentResponse()) {
      return;
    }

    const answerText = (this.sseFullMessageText || (typeof eventData['text'] === 'string' ? eventData['text'] : '')).trim();
    const question = this.sseCurrentQuestion.trim();
    const threadId = (
      this.sseLatestThreadId ||
      this.getCurrentAgentThreadId() ||
      (typeof eventData['thread_id'] === 'string' ? eventData['thread_id'] : '')
    ).trim();
    const tenantId = (environment.TenantId || '').trim();
    const userId = ((this.chatbotComponentRef?.UserID) || environment.LoginUserID || '').trim();

    const meta = extractStreamCompletionMeta(
      eventData,
      this.agentApiService.getSupervisorForAgent(this.agentId),
      this.agentConfigService.getAgentConfig(this.agentId)?.agents
    );
    if (this.agentId === 'regulations_agent' && !meta.message_id) {
      meta.message_id = meta.response_id || this.sseResponseId;
    }
    const omitSourceMetadata = this.agentId === 'regulations_agent';
    if (omitSourceMetadata) {
      delete meta.source_metadata;
    }

    const streamedMessageId = meta.message_id || meta.response_id;
    if (streamedMessageId) {
      this.sseResponseId = streamedMessageId;
      this.agentOrchestrator.setLatestMessageResponseId(streamedMessageId);
    }

    const payload = buildSaveAgentResponsePayload({
      thread_id: threadId,
      tenant_id: tenantId,
      user_id: userId,
      question,
      answerText,
      thinking: isRegulationsAgent(this.agentId) ? this.getStreamedThinkingText() : undefined,
      imageVisualization: this.sseImageVisualization.trim() || undefined,
      sourceMetadata: omitSourceMetadata ? undefined : this.buildStreamSourceMetadata(),
      meta
    });

    if (!payload) {
      this.logger.logInfo(
        '[AgentChatContainer] Skipping save_agent_response – missing required fields',
        'triggerSaveAgentResponseFromStream',
        JSON.stringify({ threadId: !!threadId, tenantId: !!tenantId, userId: !!userId, question: !!question, answerText: !!answerText })
      );
      return;
    }

    this.sseSaveTriggered = true;
    this.isConversationSaved = true;

    this.logger.logInfo(
      `[AgentChatContainer] Saving ${this.agentId} response – thread: ${threadId}, agents: ${JSON.stringify(payload.agents)}`,
      'triggerSaveAgentResponseFromStream'
    );

    this.saveAgentResponseService.saveAgentResponse(payload, this.agentId).subscribe({
      next: (result) => {
        if (result.response_id) {
          this.sseResponseId = result.response_id;
          this.agentOrchestrator.setLatestMessageResponseId(result.response_id);
          this.logger.logInfo(
            `[AgentChatContainer] save_agent_response succeeded – response_id: ${result.response_id}`,
            'triggerSaveAgentResponseFromStream'
          );
        }
      }
    });
  }

  private buildStreamSourceMetadata(): {
    references?: Record<string, unknown>;
    follow_up_questions?: string[];
  } | undefined {
    const references = this.sseMetadata.reduce<Record<string, unknown>>((result, item, index) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) {
        return result;
      }

      const source = item as Record<string, unknown>;
      const sourceKey = typeof source['key'] === 'string' && source['key'].trim().length > 0
        ? source['key'].trim()
        : String(index + 1);
      const sourceWithoutUiKey = { ...source };
      delete sourceWithoutUiKey['key'];
      result[sourceKey] = sourceWithoutUiKey;
      return result;
    }, {});

    const sourceMetadata: {
      references?: Record<string, unknown>;
      follow_up_questions?: string[];
    } = {};
    if (Object.keys(references).length > 0) {
      sourceMetadata.references = references;
    }
    if (this.ssePills.length > 0) {
      sourceMetadata.follow_up_questions = [...this.ssePills];
    }

    return Object.keys(sourceMetadata).length > 0 ? sourceMetadata : undefined;
  }

  /**
   * Handle conversation_saved event
   * For regulation agent, stop the loader when conversation is saved
   */
  private handleConversationSavedEvent(eventData: any): void {
    this.logger.logInfo('[AgentChatContainer] Conversation saved event received', 'handleConversationSavedEvent', JSON.stringify(eventData));
    
    // Mark conversation as saved
    this.isConversationSaved = true;
    
    // For regulation agent, stop the loader when conversation_saved is received
    if (this.agentId === 'regulations_agent' && eventData.conversation_saved === true) {
      this.logger.logInfo('[AgentChatContainer] Stopping loader for regulation agent after conversation_saved', 'handleConversationSavedEvent', '');
      
      // Stop the loader and streaming state
      this.internalLoaderShow = false;
      this.streamingStateService.setStreamingState(false);
      this.isResponseStreaming = false;
      this.isMetadataLoading = false;
      
      // Complete engaging loader if active
      if (this.showEngagingLoader) {
        this.engagingLoaderIsStreaming = false;
        this.engagingLoaderIsCompleted = true;
        this.engagingLoaderCurrentAction = '';
      }
      
      // Update UI to reflect completion
      this.updateSSEUI(false, true);
      
      // Force change detection
      this.cdr.detectChanges();
    }
    
    // Capture response_id if provided
    if (eventData.response_id) {
      this.sseResponseId = String(eventData.response_id);
      this.agentOrchestrator.setLatestMessageResponseId(this.sseResponseId);
      this.logger.logInfo(
        `[AgentChatContainer] Captured response_id from conversation_saved event: ${this.sseResponseId}`,
        'handleConversationSavedEvent'
      );
    }
  }

  /**
   * Scroll chat to bottom
   */
  private scrollToBottom(): void {
    try {
      setTimeout(() => {
        if (this.chatHistoryWrapper?.nativeElement) {
          const wrapper = this.chatHistoryWrapper.nativeElement as HTMLElement;
          requestAnimationFrame(() => {
            const maxScroll = wrapper.scrollHeight - wrapper.clientHeight;
            wrapper.scrollTop = maxScroll;
            
            setTimeout(() => {
              const currentScroll = wrapper.scrollTop;
              const expectedScroll = wrapper.scrollHeight - wrapper.clientHeight;
              if (Math.abs(currentScroll - expectedScroll) > 10) {
                wrapper.scrollTop = expectedScroll;
              }
            }, 50);
          });
        }
      }, 50);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error scrolling to bottom', 'scrollToBottom', error);
    }
  }

/**
   * Trigger auto-scroll only while the SSE layer reports active streaming for this update.
   * Only skip when user has deliberately scrolled up (userHasScrolledUp).
   * Do not skip based on "away from bottom" position: that can be true when we are lagging
   * (e.g. container was null on first chunks or content grew fast), and would prevent auto-scroll from ever catching up.
   */
private triggerAutoScroll(isActivelyStreaming: boolean): void {
  try {
    if (!isActivelyStreaming) {
      return;
    }
    // Ensure container is resolved so scrollToBottom can run (e.g. after first chunk before ngAfterViewInit).
    this.chatAutoScrollService.findChatContainer();
    const scrollState = this.chatAutoScrollService.getScrollState();
    
    if (scrollState.userHasScrolledUp) {
      return;
    }
    // Use instant scroll ('auto') during streaming so the view actually follows; smooth scroll on every chunk never completes.
    this.chatAutoScrollService.scrollToBottom('auto');
  } catch (error: any) {
    this.logger.logInfo('[AgentChatContainer] Error triggering auto scroll', 'triggerAutoScroll', error);
  }
}


  /**
   * Get current agent thread/session ID
   * 
   * @returns Current thread ID or empty string
   */
  private getCurrentAgentThreadId(): string {
    try {
      const orchestratorThreadId = this.agentOrchestrator.getCurrentThreadId();
      // When in new-chat state with no orchestrator thread, always treat as no session (e.g. after hub → agent)
      if (this.isNewAgentChat && !orchestratorThreadId) {
        return '';
      }
      if (orchestratorThreadId) {
        this.currentAgentThreadId = orchestratorThreadId; // Update local cache
        return orchestratorThreadId;
      } else if (this.currentAgentThreadId) {
        return this.currentAgentThreadId;
      }
      const hasMessages = this.chatHistory.length > 0;
      return hasMessages ? 'active_session' : '';
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error getting thread ID', 'getCurrentAgentThreadId', error);
      return '';
    }
  }

  /**
   * Create new agent session and add to UI immediately (better UX)
   * 
   * @param question - First question that triggers session creation
   */
  private createNewAgentSession(question: string): void {
    try {
      const newThreadId = this.generateThreadId();
      const sessionName = this.getOptimizedSessionName(question);
      
      this.currentAgentThreadId = newThreadId;
      this.currentAgentSessionName = sessionName;
      this.sessionCreated = true; // Mark as created immediately
      
      // Add session to UI immediately for better UX (like existing chatbot)
      this.agentOrchestrator.triggerNewSessionCreation(sessionName, newThreadId);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error creating new session', 'createNewAgentSession', error);
    }
  }

  /**
   * Generate unique thread ID in UUID v4 format
   * Used for SSE API requests that require UUID format thread_id
   * 
   * @returns Generated UUID v4 thread ID (e.g., "550e8400-e29b-41d4-a716-446655440000")
   */
  private generateThreadId(): string {
    try {
      // SECURITY: Primary method - use native crypto.randomUUID() (cryptographically secure)
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
      }

      // SECURITY: Fallback to crypto.getRandomValues() (cryptographically secure)
      if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        const array = new Uint8Array(16);
        crypto.getRandomValues(array);

        const hex = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');

        return [
          hex.slice(0, 8),
          hex.slice(8, 12),
          '4' + hex.slice(13, 16), // Version 4 UUID
          ((parseInt(hex.slice(16, 17), 16) & 0x3) | 0x8).toString(16) + hex.slice(17, 20), // Variant bits
          hex.slice(20, 32)
        ].join('-');
      }

      // SECURITY: Last resort for legacy browsers - use timestamp + counter
      // Not cryptographically secure but better than Math.random()
      this.logger.logInfo('[AgentChatContainer] Using timestamp-based UUID (legacy browser)', 'generateThreadId');
      return this.generateTimestampBasedUUID();
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error generating thread ID', 'generateThreadId', error);
      // SECURITY: Fallback to timestamp-based UUID instead of Math.random()
      return this.generateTimestampBasedUUID();
    }
  }

  /**
   * Generate timestamp-based UUID for legacy browsers
   * Uses timestamp + performance counter for uniqueness
   * 
   * SECURITY WARNING: Not cryptographically secure - only for legacy browser fallback
   * Should only execute in very old browsers where crypto APIs are unavailable
   * 
   * @returns Timestamp-based UUID v4 format
   */
  private generateTimestampBasedUUID(): string {
    const timestamp = Date.now();
    const perfNow = (typeof performance !== 'undefined' && performance.now) 
      ? performance.now() 
      : 0;
    
    let d = timestamp;
    let d2 = (perfNow * 1000) | 0;
    
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      let r;
      if (d > 0) {
        r = (d + d2) % 16 | 0;
        d = Math.floor(d / 16);
      } else {
        r = (d2) % 16 | 0;
        d2 = Math.floor(d2 / 16);
      }
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Get optimized session name from question
   *
   * @param question - User question
   * @returns Optimized session name (max 50 chars)
   */
  private getOptimizedSessionName(question: string): string {
    try {
      if (!question || question.trim() === '') {
        return 'New Agent Chat';
      }
      return question.trim() || 'New Agent Chat';

    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error optimizing session name', 'getOptimizedSessionName', error);
      return 'New Agent Chat';
    }
  }

  /**
   * Handle load more history event from agent-chat-interface.
   * Directly fetches the next page of history and prepends older messages.
   */
  onLoadMoreHistory(): void {
    try {
      if (!this.hasMoreHistoryPages) {
        this.agentOrchestrator.updateLazyLoadState(false, false);
        return;
      }

      if (this.isLoadingMoreHistory) {
        return;
      }

      const pagination = this.agentOrchestrator.getHistoryPagination();
      const nextPage = pagination.currentPage + 1;
      const threadId = this.agentOrchestrator.getCurrentThreadId();

      if (!threadId) {
        return;
      }

      const userID = (this.chatbotComponentRef?.UserID) || environment.LoginUserID || '';
      const tenantID = environment.TenantId || '';

      this.isLazyLoadingMore = true;
      this.agentOrchestrator.updateLazyLoadState(true, true);

      const request: GetAgentResponseHistoryRequest = {
        thread_id: threadId,
        user_id: userID,
        tenant_id: tenantID,
        page: nextPage,
        page_size: 5
      };

      const sub = this.getAgentResponseHistoryService.getResponseHistory(request, this.agentId).subscribe({
        next: (response) => {
          if (response.status === 'success' && response.data) {
            const olderMessages: AgentChatMessage[] = (response.data.responses || [])
              .map((resp: any) =>
                mapHistoryResponseToAgentMessage(
                  resp,
                  resp.image_visualization || this.extractImageVisualizationFromResponse(resp)
                )
              )
              .reverse();

            const mergeOlderMessages = (resolvedOlder: AgentChatMessage[]): void => {
              if (resolvedOlder.length > 0) {
                const currentHistory = this.agentOrchestrator.getAgentChatHistory();
                const merged = [...resolvedOlder, ...currentHistory];

                let scrollContainer = this.chatAutoScrollService.getChatContainer();
                if (!scrollContainer && this.agentChatInterface?.scrollTarget) {
                  this.chatAutoScrollService.findChatContainer(
                    undefined,
                    this.agentChatInterface.scrollTarget
                  );
                  scrollContainer = this.chatAutoScrollService.getChatContainer();
                }
                const oldScrollHeight = scrollContainer ? scrollContainer.scrollHeight : 0;
                const oldScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

                this.agentOrchestrator.updateChatHistory(merged);

                if (scrollContainer && oldScrollHeight > 0) {
                  const restoreScroll = (): void => {
                    if (!scrollContainer) return;
                    const newScrollHeight = scrollContainer.scrollHeight;
                    const delta = newScrollHeight - oldScrollHeight;
                    if (delta > 0) {
                      scrollContainer!.scrollTop = oldScrollTop + delta;
                    }
                  };
                  setTimeout(restoreScroll, 0);
                  setTimeout(restoreScroll, 50);
                  setTimeout(restoreScroll, 150);
                }
              }

              const newCurrentPage = response.data!.pagination?.current_page ?? nextPage;
              const newTotalPages = response.data!.pagination?.total_pages ?? pagination.totalPages;
              this.agentOrchestrator.updateHistoryPagination(newCurrentPage, newTotalPages);
              const hasMore = newCurrentPage < newTotalPages;
              if (resolvedOlder.length > 0) {
                setTimeout(() => {
                  this.agentOrchestrator.updateLazyLoadState(false, hasMore);
                  this.isLazyLoadingMore = false;
                }, 300);
              } else {
                this.agentOrchestrator.updateLazyLoadState(false, hasMore);
                this.isLazyLoadingMore = false;
              }
            };

            mergeOlderMessages(olderMessages);
          } else {
            this.agentOrchestrator.updateLazyLoadState(false, false);
            this.isLazyLoadingMore = false;
          }
        },
        error: () => {
          this.agentOrchestrator.updateLazyLoadState(false, false);
          this.isLazyLoadingMore = false;
        }
      });

      this.subscriptions.push(sub);
    } catch (error: any) {
      this.logger.logInfo('[AgentChatContainer] Error handling load more history', 'onLoadMoreHistory', error);
      this.isLazyLoadingMore = false;
    }
  }
}
